<template>
  <div class="recommendations-container">
    <!-- Header -->
    <div class="rec-header">
      <div>
        <h2 class="rec-header__title">{{ $t('orgDashboard.users.talentRecommendations') }}</h2>
        <p class="rec-header__subtitle">{{ $t('orgDashboard.users.recommendationsDescription') }}</p>
      </div>
      <div class="rec-header__actions">
        <vs-tooltip text="How it works" position="top">
          <button class="rec-header__help" @click="showGuidesModal = true">
            <i class="feather icon-help-circle"></i> {{ $t('orgDashboard.users.guide') }}
          </button>
        </vs-tooltip>
        <vs-tooltip :text="viewMode === 'simple' ? 'Detailed View' : 'Simple View'" position="top">
          <button class="view-toggle-btn" @click="viewMode = viewMode === 'simple' ? 'detailed' : 'simple'">
            <i class="feather" :class="viewMode === 'simple' ? 'icon-grid' : 'icon-list'"></i>
          </button>
        </vs-tooltip>
      </div>
    </div>

    <!-- SIMPLE VIEW -->
    <div v-if="viewMode === 'simple'" class="plan-list">
      <!-- FREE: Hiring Plan -->
      <div class="plan-list__item" @click="openActivationModal('Hiring Plan', 'plan_hiring_001', 'briefcase.svg')">
        <div class="plan-list__icon plan-list__icon--hiring">
          <i class="feather icon-briefcase" style="font-size: 1.5rem;"></i>
        </div>
        <div class="plan-list__info">
          <h3 class="plan-list__name">{{ $t('orgDashboard.users.hiringPlan') }}  <span class="plan-badge plan-badge--free">FREE</span></h3>
          <span class="plan-list__desc">{{ $t('orgDashboard.users.hiringPlanDescription') }}</span>
        </div>
        <i class="feather icon-chevron-right plan-list__arrow"></i>
      </div>

      <!-- PAID Plans -->
      <div class="plan-list__item" @click="openActivationModal('Fitment Plan', 'plan_fitment_001', 'coaching.svg')">
        <div class="plan-list__icon plan-list__icon--fitment">
          <img src="@/assets/Iconography/App Icons/coaching.svg" alt="Coaching" class="w-6 h-6 object-contain">
        </div>
        <div class="plan-list__info">
          <h3 class="plan-list__name">{{ $t('orgDashboard.users.fitmentPlan') }} <span class="plan-badge plan-badge--paid">PAID</span></h3>
          <span class="plan-list__desc">{{ $t('orgDashboard.users.fitmentPlanDescription') }}</span>
        </div>
        <i class="feather icon-chevron-right plan-list__arrow"></i>
      </div>

      <div class="plan-list__item" @click="showContactModal = true">
        <div class="plan-list__icon plan-list__icon--development">
          <img src="@/assets/Iconography/App Icons/bot.svg" alt="ALEX Bot" class="w-6 h-6 object-contain">
        </div>
        <div class="plan-list__info">
          <h3 class="plan-list__name">{{ $t('orgDashboard.users.developmentPlan') }} <span class="badge-paid">PAID</span></h3>
          <span class="plan-list__desc">{{ $t('orgDashboard.users.developmentPlanDescription') }}</span>
        </div>
        <i class="feather icon-chevron-right plan-list__arrow"></i>
      </div>

      <!-- Succession Planning -->
      <div class="plan-list__item" @click="openSuccessionWizard"> <!-- CHANGED HERE -->
        <div class="plan-list__icon plan-list__icon--succession">
          <img src="@/assets/Iconography/App Icons/rewards.svg" alt="Rewards" class="w-6 h-6 object-contain">
        </div>
        <div class="plan-list__info">
          <h3 class="plan-list__name">{{ $t('orgDashboard.users.successionPlanning') }} <span class="badge-paid">PAID</span></h3>
          <span class="plan-list__desc">{{ $t('orgDashboard.users.successionPlanningDescription') }}</span>
        </div>
        <i class="feather icon-chevron-right plan-list__arrow"></i>
      </div>
    </div>

    <!-- DETAILED VIEW -->
    <div v-else-if="viewMode === 'detailed'" class="plan-cards">
      <!-- FREE: Hiring Plan -->
      <div class="plan-card plan-card--hiring">
        <div class="plan-card__icon">
          <i class="feather icon-briefcase"></i>
        </div>
        <h3 class="plan-card__title">{{ $t('orgDashboard.users.hiringPlan') }} <span class="plan-badge plan-badge--free">{{ $t('orgDashboard.users.free') }}</span></h3>
        <span class="plan-card__subtitle">{{ $t('orgDashboard.users.hiringPlanDescription') }}</span>
        <p class="plan-card__desc">{{ $t('orgDashboard.users.hiringPlanDescription1') }}</p>
        <ul class="plan-card__features">
          <li><i class="feather icon-check"></i>{{ $t('orgDashboard.users.hiringPlanContent1') }}</li>
          <li><i class="feather icon-check"></i>{{ $t('orgDashboard.users.hiringPlanContent2') }}</li>
          <li><i class="feather icon-check"></i>{{ $t('orgDashboard.users.hiringPlanContent3') }}</li>
        </ul>
        <button class="plan-card__btn plan-card__btn--hiring" @click="openActivationModal('Hiring Plan', 'plan_hiring_001', 'briefcase.svg')">
          {{ $t('orgDashboard.users.starthiring') }} <i class="feather icon-arrow-right ml-2"></i>
        </button>
      </div>

      <!-- PAID: Fitment -->
      <div class="plan-card plan-card--fitment">
        <div class="plan-card__icon">
          <img src="@/assets/Iconography/App Icons/coaching.svg" alt="Coaching">
        </div>
        <h3 class="plan-card__title">{{ $t('orgDashboard.users.fitmentPlan') }} <span class="plan-badge plan-badge--paid">{{ $t('orgDashboard.users.paid') }}</span></h3>
        <span class="plan-card__subtitle">{{ $t('orgDashboard.users.fitmentPlanDescription') }}</span>
        <p class="plan-card__desc">{{ $t('orgDashboard.users.fitmentPlanContent') }}</p>
        <ul class="plan-card__features">
          <li><i class="feather icon-check"></i>{{ $t('orgDashboard.users.roadToRole') }}</li>
          <li><i class="feather icon-check"></i>{{ $t('orgDashboard.users.skillProfiling') }}</li>
          <li><i class="feather icon-check"></i>{{ $t('orgDashboard.users.learningCredits') }}</li>
        </ul>
        <button class="plan-card__btn plan-card__btn--fitment" @click="openActivationModal('Fitment Plan', 'plan_fitment_001', 'coaching.svg')">
          {{ $t('orgDashboard.users.startonboarding') }} <i class="feather icon-arrow-right ml-2"></i>
        </button>
      </div>

      <!-- PAID: Development -->
      <div class="plan-card plan-card--development">
        <div class="plan-card__icon">
          <img src="@/assets/Iconography/App Icons/bot.svg" alt="ALEX Bot">
        </div>
        <h3 class="plan-card__title">{{ $t('orgDashboard.users.developmentPlan') }} <span class="badge-paid">{{ $t('orgDashboard.users.paid') }}</span></h3>
        <span class="plan-card__subtitle">{{ $t('orgDashboard.users.developmentPlanDescription') }}</span>
        <p class="plan-card__desc">{{ $t('orgDashboard.users.developmentPlanContent') }}</p>
        <ul class="plan-card__features">
          <li><i class="feather icon-check"></i>{{ $t('orgDashboard.users.customLearningPaths') }}</li>
          <li><i class="feather icon-check"></i>{{ $t('orgDashboard.users.aiAssistedValidation') }}</li>
          <li><i class="feather icon-check"></i>{{ $t('orgDashboard.users.progressTracking') }}</li>
        </ul>
        <button class="plan-card__btn plan-card__btn--development" @click="showContactModal = true">
          {{ $t('orgDashboard.users.startdevelopment') }} <i class="feather icon-arrow-right ml-2"></i>
        </button>
      </div>

      <!-- PAID: Succession -->
      <div class="plan-card plan-card--succession">
        <div class="plan-card__icon">
          <img src="@/assets/Iconography/App Icons/rewards.svg" alt="Rewards">
        </div>
        <h3 class="plan-card__title">{{ $t('orgDashboard.users.successionPlanning') }} <span class="plan-badge plan-badge--paid">{{ $t('orgDashboard.users.paid') }}</span></h3>
        <span class="plan-card__subtitle">{{ $t('orgDashboard.users.successionPlanningDescription') }}</span>
        <p class="plan-card__desc">{{ $t('orgDashboard.users.successionPlanningContent') }}</p>
        <ul class="plan-card__features">
          <li><i class="feather icon-check"></i>{{ $t('orgDashboard.users.goalSettingMentorship') }}</li>
          <li><i class="feather icon-check"></i>{{ $t('orgDashboard.users.behaviouralCoaching') }}</li>
          <li><i class="feather icon-check"></i>{{ $t('orgDashboard.users.careersuccession') }}</li>
        </ul>
        <button class="plan-card__btn plan-card__btn--succession" @click="openSuccessionWizard"> <!-- CHANGED HERE -->
          {{ $t('orgDashboard.users.startcoaching') }} <i class="feather icon-arrow-right ml-2"></i>
        </button>
      </div>
    </div>

    <!-- Bottom section: plans, guides, or loading -->
    <div v-if="plansState === 'loading' || plansState === 'has-plans'" class="active-plans">
      <div class="active-plans__header">
        <div>
          <h3 class="active-plans__title">{{ $t('orgDashboard.users.myplans') }}</h3>
          <p class="active-plans__subtitle">{{ $t('orgDashboard.users.workspaces') }}</p>
        </div>
        <span v-if="activePlans.length + pendingPlans.length > 0" class="active-plans__badge">{{ activePlans.length + pendingPlans.length }} {{ (activePlans.length + pendingPlans.length) === 1 ? 'Plan' : 'Plans' }}</span>
      </div>

      <div v-if="plansState === 'loading'" class="active-plans__loading">
        <div class="active-plans__loader">
          <i class="feather icon-loader fa-spin"></i>
        </div>
        <span>{{ $t('orgDashboard.users.loadingplans') }}</span>
      </div>

      <div v-else class="active-plans__list">
        <!-- Active plans -->
        <div v-for="(plan, index) in activePlans" :key="'active-' + index" class="ap-row" @click="plan.isCoachPlan ? handleCoachBoardClick(plan) : goToBoard(plan)">          
          <div class="ap-row__left">
            <div class="ap-row__icon" :class="getPlanThemeClass(plan)">
              <i class="feather" :class="getPlanIcon(plan)"></i>
            </div>

            <div class="ap-row__info">
              <h4 class="ap-row__name">{{ plan.title || 'Untitled Plan' }}</h4>
              <div class="ap-row__meta-wrapper">
                
                <span v-if="plan.isCoachPlan" class="ap-row__meta">
                  <i class="feather icon-pie-chart"></i> {{ plan.members ? plan.members.length : 0 }} / {{ plan.maxCredits }} Credits Used
                </span>
                
                <span v-else class="ap-row__meta">
                  <i class="feather icon-users"></i> 
                  
                  <!-- ⭐️ FIX: Show exact count from Mongo, fallback to Python SQLite -->
                  {{ plan.realMemberCount !== undefined ? plan.realMemberCount : (plan.talentAssignedUsers ? plan.talentAssignedUsers.length : (plan.members ? plan.members.length : 0)) }} / 
                  {{ plan.talentPlanLimit || plan.numberOfUsers || 10 }} participants
                  
                  <!-- Show Free or Paid tag based on Python DB -->
                  <span v-if="plan.talentPlanLimit !== undefined" class="plan-badge ml-2" :class="plan.isFree ? 'plan-badge--free' : 'plan-badge--paid'">
                    {{ plan.isFree ? 'FREE' : 'PAID' }}
                  </span>
                </span>
                
                <span class="ap-row__divider">•</span>
                <span class="ap-row__meta"><i class="feather icon-calendar"></i> Created {{ formatDate(plan.createdAt) }}</span>
              </div>
            </div>
          </div>

          <div class="ap-row__right">
            <span class="ap-row__status"><i class="feather icon-zap"></i> Active</span>
            
            <!-- GEAR ICON BUTTON -->
            <button v-if="plan.isCoachPlan" class="ap-row__manage" @click.stop="openCoachPlanManager(plan)">
              <i class="feather icon-settings"></i>
            </button>
            <button v-else class="ap-row__manage" @click.stop="openEditModal(plan)">
              <i class="feather icon-settings"></i>
            </button>

            <!-- OPEN BUTTON -->
            <button
              v-if="plan.isCoachPlan"
              
              title="Open Coach"
              class="ap-row__open icon-btn coach-icon-btn"
              @click.stop="openCoachUrl()"
            >
              <i class="feather icon-external-link"></i>
            </button>

            <button
              v-else
              
              title="Open Board"
              class="ap-row__open icon-btn"
              @click.stop="goToBoard(plan)"
            >
              <i class="feather icon-arrow-right"></i>
            </button>
          </div>
        </div>

        <!-- Pending payment plans -->
        <div v-for="(plan, index) in pendingPlans" :key="'pending-' + index" class="ap-row ap-row--pending">
          <div class="ap-row__left">
            <div class="ap-row__icon ap-row__icon--pending">
              <i class="feather icon-clock"></i>
            </div>
            <div class="ap-row__info">
              <h4 class="ap-row__name">{{ plan.title || 'Untitled Plan' }}</h4>
              <div class="ap-row__meta-wrapper">
                <span class="ap-row__meta"><i class="feather icon-users"></i> {{ plan.numberOfUsers || 0 }} participants</span>
                <span class="ap-row__divider">•</span>
                <span class="ap-row__meta"><i class="feather icon-calendar"></i> Created {{ formatDate(plan.createdAt) }}</span>
              </div>
            </div>
          </div>
          <div class="ap-row__right">
            <span class="ap-row__status ap-row__status--pending"><i class="feather icon-clock"></i> Pending Payment</span>
            <button class="ap-row__open ap-row__open--pending" @click.stop="resumePayment(plan)">
              {{ $t('orgDashboard.users.completePayment') }} <i class="feather icon-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="guides">
      <!-- Unchanged guides... -->
      <div class="guides__header">
        <h3 class="guides__title">{{ $t('orgDashboard.users.gettingstarted') }}</h3>
        <p class="guides__subtitle">{{ $t('orgDashboard.users.learnsetup') }}</p>
      </div>

      <div class="guides__grid">
        <div v-for="(video, idx) in guideVideos" :key="idx" class="guide-card">
          <div class="guide-card__thumb" @click="playingVideo === idx ? playingVideo = null : playingVideo = idx">
            <iframe v-if="playingVideo === idx" :src="'https://www.youtube.com/embed/' + video.id + '?autoplay=1'" frameborder="0" allow="autoplay; encrypted-media" allowfullscreen></iframe>
            <template v-else>
              <img :src="'https://img.youtube.com/vi/' + video.id + '/mqdefault.jpg'" :alt="video.title">
              <div class="guide-card__play"><i class="feather icon-play"></i></div>
              <span class="guide-card__duration">{{ video.duration }}</span>
            </template>
          </div>
          <div class="guide-card__body">
            <h4>{{ video.title }}</h4>
            <p>{{ video.desc }}</p>
          </div>
        </div>
      </div>

      <div class="guides__help">
        <i class="feather icon-mail"></i>
        <span> {{ $t('orgDashboard.users.needhelp') }} <a href="mailto:support@getboarded.com">support@getboarded.com</a></span>
      </div>
    </div>

    <!-- Guides Modal -->
    <vs-popup title="Getting Started Guides" :active.sync="showGuidesModal" class="guides-popup">
      <div class="guides-modal">
        
        <div v-for="(video, idx) in guideVideos" :key="idx" class="guides-modal__item guide-card">
          <div class="guide-card__thumb" @click="playingVideo === idx ? playingVideo = null : playingVideo = idx">
            <iframe v-if="playingVideo === idx" :src="'https://www.youtube.com/embed/' + video.id + '?autoplay=1'" frameborder="0" allow="autoplay; encrypted-media" allowfullscreen></iframe>
            <template v-else>
              <img :src="'https://img.youtube.com/vi/' + video.id + '/mqdefault.jpg'" :alt="video.title">
              <div class="guide-card__play"><i class="feather icon-play"></i></div>
              <span class="guide-card__duration">{{ video.duration }}</span>
            </template>
          </div>
          <div class="guide-card__body">
            <h4>{{ video.title }}</h4>
            <p>{{ video.desc }}</p>
          </div>
        </div>

        <div class="guides__help">
          <i class="feather icon-mail"></i>
          <span> {{ $t('orgDashboard.users.needhelp') }} <a href="mailto:support@getboarded.com">support@getboarded.com</a></span>
        </div>

      </div>
    </vs-popup>

    <!-- Contact / Coming Soon Modal -->
    <vs-popup :title="' '" :active.sync="showContactModal" class="contact-popup">
      <div class="contact-modal">
        <h3>{{ $t('orgDashboard.users.letsgetustarted') }}</h3>
        <p>{{ $t('orgDashboard.users.tailorplandesc') }}</p>
        
        <div class="contact-modal__actions">
          <a href="mailto:support@getboarded.com" class="contact-modal__btn contact-modal__btn--primary">
            <i class="feather icon-mail"></i> support@getboarded.com
          </a>
          <button class="contact-modal__btn contact-modal__btn--secondary" @click="showContactModal = false">
            Close
          </button>
        </div>
      </div>
    </vs-popup>

    <!-- Configuration / Manage Modal -->
  <vs-popup :title="' '" :active.sync="isModalActive" class="configure-popup">
    <div class="cfg" :class="{ 'cfg--loading': isLoadingBoardDetails }">
      <button class="cfg__close-btn" @click="closeConfigModal">
        <i class="feather icon-x"></i>
      </button>
      <!-- Loading overlay -->
      <div v-if="isLoadingBoardDetails" class="cfg__overlay">
        <i class="feather icon-loader fa-spin"></i>
        <span>{{ $t('orgDashboard.users.loadingmembers') }}</span>
      </div>

      <!-- Edit mode (Managing existing plan members) -->
      <template v-if="isEditMode">
        <div class="cfg__header">
          <h3 class="cfg__title">Manage Plan</h3>
          <p class="cfg__desc">Update the participants enrolled in this plan.</p>
        </div>
        
        <div class="cfg__section">
          <label class="cfg__label"><i class="feather icon-bookmark"></i> Plan Name</label>
          <div class="flex items-center justify-between relative">
            <p class="cfg__readonly-value m-0">{{ boardTitle || selectedPlanName || 'Coaching Plan' }}</p>
            
            <!-- Three vertical dots menu button -->
            <div class="relative">
              <button 
                class="cfg__more-btn" 
                type="button" 
                title="Plan Options"
                @click.stop="showPlanOptionsDropdown = !showPlanOptionsDropdown">
                <i class="feather icon-more-vertical"></i>
              </button>

              <!-- Custom Dropdown Menu -->
              <div v-if="showPlanOptionsDropdown" class="cfg__plan-options-menu" @click.stop>
                <div class="cfg__plan-option-item" @click="goToEditBoardDetails">
                  <i class="feather icon-edit-2 mr-2"></i> Edit Board Details
                </div>
                <div class="cfg__plan-option-item cfg__plan-option-item--danger" @click="showPlanOptionsDropdown = false; deactivationConfirmPrompt = true">
                  <i class="feather icon-trash-2 mr-2"></i> Deactivate Board
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="cfg__section cfg__section--active" :class="{'cfg__section--coach': isCoachPlan}">
          <div class="cfg__step-indicator">
            <span class="cfg__step-dot"></span> Manage members
          </div>
          
          <div class="cfg__label-row">
            <label class="cfg__label"><i class="feather icon-users"></i> Participants</label>
            
            <!-- ⭐️ FIX: Dynamic Tracker for ALL plans -->
            <div class="cfg__credit-tracker" :class="{ 'cfg__credit-tracker--full': selectedUsers.length >= (isCoachPlan ? maxCredits : planLimit) }">
              <i class="feather" :class="isCoachPlan ? 'icon-pie-chart' : 'icon-users'"></i>
              <span>{{ selectedUsers.length }} / {{ isCoachPlan ? maxCredits : planLimit }} {{ isCoachPlan ? 'Credits' : 'Limits' }} Used</span>
              <button class="cfg__add-credits-inline-btn" title="Buy Credits" @click.stop="triggerBuyCreditsFlow">
                <i class="feather icon-plus"></i>
              </button>
            </div>
          </div>

          <!-- ⭐️ FIX: Upgrade Box for ALL plans -->
          <div v-if="selectedUsers.length >= (isCoachPlan ? maxCredits : planLimit)" class="cfg__buy-credits-box">
             <i class="feather icon-alert-circle"></i> 
             <div>
               <p>You have reached your limit of <strong>{{ isCoachPlan ? maxCredits : planLimit }}</strong> participants.</p>
               <a @click.stop="triggerBuyCreditsFlow" style="cursor: pointer; text-decoration: underline;">Click here to add more</a>
             </div>
          </div>
          <p v-else class="cfg__hint">Search or filter by role to quickly find and add candidates to this plan.</p>

          <div class="cfg__controls">
            <vs-input icon="search" placeholder="Search by name or email..." v-model="userSearchQuery" class="w-full cfg__search"/>
            
            <div v-if="availableRoles.length > 0" class="cfg__filter-wrapper">
              <vs-tooltip text="Filter by role" position="top">
                <button class="cfg__filter-icon-btn" :class="{ 'cfg__filter-icon-btn--active': activeRoleFilters.length > 0 }" @click="showRoleDropdown = !showRoleDropdown">
                  <i class="feather icon-filter"></i>
                  <span v-if="activeRoleFilters.length > 0" class="cfg__filter-badge">{{ activeRoleFilters.length }}</span>
                </button>
              </vs-tooltip>

              <transition name="cfg-expand">
                <div v-if="showRoleDropdown" class="cfg__dropdown-menu">
                  <div class="cfg__dropdown-header" v-if="activeRoleFilters.length > 0">
                    <span class="cfg__dropdown-selected-text">{{ activeRoleFilters.length }} selected</span>
                    <button class="cfg__filter-clear" @click="activeRoleFilters = []">Clear</button>
                  </div>
                  <div class="cfg__dropdown-list">
                    <div v-for="(role, index) in availableRoles" :key="index" class="cfg__dropdown-item" @click="toggleRoleGroup(role)">
                      <div class="cfg__role-checkbox" :class="{ 'cfg__role-checkbox--active': activeRoleFilters.includes(role) }">
                        <i v-if="activeRoleFilters.includes(role)" class="feather icon-check"></i>
                      </div>
                      <span>{{ role }}</span>
                    </div>
                  </div>
                </div>
              </transition>
            </div>
          </div>
        </div>

        <div class="cfg__list-wrap">
          <div class="cfg__list-header">
            <span>{{ filteredUsers.length }} members</span>
          </div>
          <div class="cfg__list">
            <!-- User Row -->
            <div 
              v-for="(user, index) in filteredUsers" 
              :key="index" 
              class="cfg__user" 
              :class="{ 
                'cfg__user--selected': selectedUsers.includes(user.id),
                'cfg__user--disabled': isEditMode && remainingCredits <= 0 && !selectedUsers.includes(user.id) 
              }" 
              @click="toggleUserSelection(user)"
            >
              <div class="cfg__user-check" :class="{ 'cfg__user-check--active': selectedUsers.includes(user.id) }">
                <i v-if="selectedUsers.includes(user.id)" class="feather icon-check"></i>
              </div>
              <div class="cfg__user-info">
                <p class="cfg__user-name">{{ user.name }}</p>
                <div class="cfg__user-details">
                  <span v-if="user.role" class="cfg__user-role">{{ user.role }}</span>
                  <span class="cfg__user-email">{{ user.email }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="cfg__footer">
          <button class="cfg__cancel" @click="closeConfigModal">Cancel</button>
          <button class="cfg__submit" :class="{'cfg__submit--coach': isCoachPlan}" @click="confirmActivation()" :disabled="isLoading || isLoadingBoardDetails || selectedUsers.length === 0">
            <template v-if="isLoading"><i class="feather icon-loader fa-spin"></i> Updating...</template>
            <template v-else><i class="feather icon-save"></i> Update Members</template>
          </button>
          <SuccessionPlanWizard 
      :active.sync="showSuccessionWizard" 
      :orgData="orgData" 
    />
        </div>
      </template>

      <!-- Create mode: AI-assisted wizard (For ALL Plans) -->
      <template v-else>
        <!-- Progress dots -->
        <div v-if="!isResumePayment" class="wiz__dots">
          <div class="wiz__dot" :class="{ 'wiz__dot--done': wizardStep > 1, 'wiz__dot--current': wizardStep === 1 }">
            <span><i v-if="wizardStep > 1" class="feather icon-check"></i><template v-else>1</template></span>
            <p>Describe</p>
          </div>
          <div class="wiz__dot-line" :class="{ 'wiz__dot-line--active': wizardStep >= 2 }"></div>
          <div class="wiz__dot" :class="{ 'wiz__dot--done': wizardStep > 2, 'wiz__dot--current': wizardStep === 2 }">
            <span><i v-if="wizardStep > 2" class="feather icon-check"></i><template v-else>2</template></span>
            <p>Review</p>
          </div>
          <div class="wiz__dot-line" :class="{ 'wiz__dot-line--active': wizardStep >= 3 }"></div>
          <div class="wiz__dot" :class="{ 'wiz__dot--current': wizardStep === 3 }">
            <span>3</span>
            <p>Launch</p>
          </div>
        </div>

        <!-- Step 1: Describe your use case -->
        <div v-if="wizardStep === 1" class="wiz__step">
          <div class="wiz__hero">
            <h3 class="wiz__title">{{ $t('orgDashboard.users.describeGoal') }}</h3>
            <p class="wiz__subtitle">{{ $t('orgDashboard.users.describeParticipants') }}</p>
          </div>

          <div class="wiz__input-group">
            <textarea ref="useCaseField" class="wiz__textarea wiz__textarea--hero" v-model="aiUseCase" rows="5" maxlength="500" :placeholder="'For example:\n\n\'We\'re onboarding 15 new hires. They need to complete a skills assessment, get matched with a mentor, finish compliance training, and set up their workspace before starting.\''"></textarea>
            <div class="wiz__textarea-footer">
              <span class="wiz__char-count" :class="{ 'wiz__char-count--warn': aiUseCase.length > 450 }">{{ aiUseCase.length }}/500</span>
            </div>
          </div>

          <div class="wiz__suggestions" v-if="!aiUseCase">
              <p class="wiz__suggestions-label">{{ $t('orgDashboard.users.starttemplate') }}</p>
              
              <!-- Templates for Hiring Plan -->
              <div v-if="selectedPlanName === 'Hiring Plan'" class="wiz__suggestion-chips">
                <button class="wiz__suggestion" @click="applyTemplate('onboarding1')">
                  <i class="feather icon-user-plus"></i> Online Interviews
                </button>
                <button class="wiz__suggestion" @click="applyTemplate('onboarding2')">
                  <i class="feather icon-target"></i> In-Person Interviews
                </button>
                <button class="wiz__suggestion" @click="applyTemplate('onboarding3')">
                  <i class="feather icon-trending-up"></i> Hiring and Offer
                </button>
              </div>

              <!-- Templates for Fitment Plan -->
              <div v-else class="wiz__suggestion-chips">
                <button class="wiz__suggestion" @click="applyTemplate('onboarding')">
                  <i class="feather icon-user-plus"></i> New hire onboarding
                </button>
                <button class="wiz__suggestion" @click="applyTemplate('fitment')">
                  <i class="feather icon-target"></i> Role fitment check
                </button>
                <button class="wiz__suggestion" @click="applyTemplate('review')">
                  <i class="feather icon-trending-up"></i> Talent review cycle
                </button>
              </div>
          </div>
        </div>

        <!-- Step 2: Journey preview (Now with invisible scroll wrapper) -->
        <div v-if="wizardStep === 2" class="wiz__step">
          <div class="wiz__step-scrollable">
            <div v-if="aiGenerating" class="wiz__generating">
              <div class="wiz__generating-pulse"></div>
              <p class="wiz__generating-text">{{ $t('orgDashboard.users.creatingJourney') }}</p>
              <span class="wiz__generating-sub">{{ $t('orgDashboard.users.designingSteps') }}</span>
            </div>

            <template v-else>
              <div class="wiz__hero wiz__hero--compact">
                <h3 class="wiz__title">{{ $t('orgDashboard.users.designedjourney') }}</h3>
                <p class="wiz__subtitle">{{ $t('orgDashboard.users.designedjournerydesc') }}</p>
              </div>

              <!-- Suggested plan name -->
              <div class="wiz__name-suggestion">
                <label class="wiz__label">{{ $t('orgDashboard.users.suggestedname') }}</label>
                <input type="text" class="wiz__input" v-model="boardTitle" placeholder="Plan name" />
              </div>

              <!-- Visual journey timeline -->
              <div class="wiz__journey">
                <div v-for="(col, idx) in aiGeneratedCols" :key="idx" class="wiz__jstep" :class="{ 'wiz__jstep--last': idx === aiGeneratedCols.length - 1 }">
                  <div class="wiz__jstep-marker">
                    <div class="wiz__jstep-circle">
                      <i class="feather" :class="idx === aiGeneratedCols.length - 1 ? 'icon-flag' : 'icon-check'"></i>
                    </div>
                    <div v-if="idx < aiGeneratedCols.length - 1" class="wiz__jstep-line"></div>
                  </div>
                  <div class="wiz__jstep-content">
                    <div class="wiz__jstep-header">
                      <span class="wiz__jstep-num">{{ $t('orgDashboard.users.step') }} {{ idx + 1 }}</span>
                      <button class="wiz__jstep-remove" @click="aiGeneratedCols.splice(idx, 1)" v-if="aiGeneratedCols.length > 2" title="Remove step">
                        <i class="feather icon-trash-2"></i>
                      </button>
                    </div>
                    <input type="text" class="wiz__jstep-title" v-model="col.title" />
                    <input type="text" class="wiz__jstep-desc-edit" v-model="col.description" placeholder="Add a description..." />
                  </div>
                </div>

                <div class="wiz__jstep wiz__jstep--add">
                  <div class="wiz__jstep-marker">
                    <div class="wiz__jstep-circle wiz__jstep-circle--add">
                      <i class="feather icon-plus"></i>
                    </div>
                  </div>
                  <button class="wiz__jstep-add-btn" @click="aiGeneratedCols.splice(aiGeneratedCols.length - 1, 0, { title: 'New Step', description: '' })">
                    {{ $t('orgDashboard.users.addstep') }}
                  </button>
                </div>
              </div>

              <div class="wiz__journey-hint">
                <i class="feather icon-info"></i>
                <span>{{ $t('orgDashboard.users.journeyHint') }}</span>
              </div>
            </template>
          </div>
        </div>

          <div v-if="wizardStep === 3" class="wiz__step">
          <div class="wiz__step-scrollable"> <!-- ADDED: Wrapper to prevent dropdown leak -->
            
            <template v-if="isResumePayment">
              <div class="wiz__hero" style="padding-top: 1.5rem;">
                <div class="wiz__hero-icon">
                  <i class="feather icon-credit-card"></i>
                </div>
                <h3 class="wiz__title">{{ $t('orgDashboard.users.completePayment') }}</h3>
                <p class="wiz__subtitle">{{ $t('orgDashboard.users.finishPayment') }}</p>
              </div>

              <div class="wiz__summary">
                <h4 class="wiz__summary-title">{{ $t('orgDashboard.users.ordersummary') }}</h4>
                <div class="wiz__summary-row">
                  <span><i class="feather icon-layers"></i> {{ boardTitle }}</span>
                </div>
                <div class="wiz__summary-row">
                  <span><i class="feather icon-users"></i> {{ wizardTeamSize }} {{ wizardTeamSize === 1 ? 'participant' : 'participants' }}</span>
                </div>
                <div class="wiz__summary-divider"></div>
                <div class="wiz__summary-row wiz__summary-row--total">
                  <span>Total</span>
                  <span class="wiz__pricing-total">&euro;{{ (wizardTeamSize * 5).toFixed(2) }}</span>
                </div>
              </div>
            </template>

            <template v-else>
              <div class="wiz__hero wiz__hero--compact">
                <h3 class="wiz__title">{{ $t('orgDashboard.users.howManyParticipants') }}</h3>
                <p class="wiz__subtitle">{{ $t('orgDashboard.users.setNumberOfPeople') }}</p>
              </div>

              <div class="wiz__team-size">
                <div class="wiz__size-control">
                  <button class="wiz__size-btn" @click="wizardTeamSize = Math.max(selectedUsers.length || 1, wizardTeamSize - 1)">
                    <i class="feather icon-minus"></i>
                  </button>
                  <input type="number" class="wiz__size-input" v-model.number="wizardTeamSize" min="1" max="500" />
                  <button class="wiz__size-btn" @click="wizardTeamSize = Math.min(500, wizardTeamSize + 1)">
                    <i class="feather icon-plus"></i>
                  </button>
                </div>
                <span class="wiz__size-label">{{ $t('orgDashboard.users.participants') }}</span>
              </div>

              <div class="wiz__quick-sizes">
                <button v-for="size in [5, 10, 25, 50]" :key="size" class="wiz__quick-size" :class="{ 'wiz__quick-size--active': wizardTeamSize === size, 'wiz__quick-size--disabled': size < selectedUsers.length }" :disabled="size < selectedUsers.length" @click="wizardTeamSize = size">
                  {{ size }}
                </button>
              </div>

              <!-- Summary card -->
              <div class="wiz__summary">
                <h4 class="wiz__summary-title">{{ $t('orgDashboard.users.plansummary') }}</h4>
                <div class="wiz__summary-row">
                  <span><i class="feather icon-layers"></i> {{ boardTitle }}</span>
                </div>
                <div class="wiz__summary-row">
                  <span><i class="feather icon-git-merge"></i> {{ aiGeneratedCols.length }} {{ $t('orgDashboard.users.stepsinjourney') }}</span>
                </div>
                <div class="wiz__summary-row">
                  <span><i class="feather icon-users"></i> {{ wizardTeamSize }} {{ wizardTeamSize === 1 ? 'participant' : 'participants' }}</span>
                </div>
                
                <template v-if="!isCurrentPlanFree">
                  <div class="wiz__summary-divider"></div>
                  <div class="wiz__summary-row wiz__summary-row--total">
                    <span>{{ $t('orgDashboard.users.total') }}</span>
                    <span class="wiz__pricing-total">&euro;{{ (wizardTeamSize * 5).toFixed(2) }}</span>
                  </div>
                </template>
              </div>

              <!-- Optional: add specific people now -->
              <div class="wiz__people-section">
                <button class="wiz__people-toggle" @click="showParticipantPicker = !showParticipantPicker">
                  <div class="wiz__people-toggle-left">
                    <i class="feather icon-user-plus"></i>
                    <span>{{ showParticipantPicker ? 'Hide participant list' : 'Add specific people now' }}</span>
                  </div>
                  <div class="wiz__people-toggle-right">
                    <span v-if="selectedUsers.length > 0" class="wiz__people-count">{{ selectedUsers.length }} {{ $t('orgDashboard.users.added') }}</span>
                    <i class="feather" :class="showParticipantPicker ? 'icon-chevron-up' : 'icon-chevron-down'"></i>
                  </div>
                </button>

                <transition name="cfg-expand">
                  <div v-if="showParticipantPicker" class="wiz__people-picker">
                    <div class="wiz__people-search">
                      <i class="feather icon-search"></i>
                      <input type="text" v-model="userSearchQuery" placeholder="Search by name or email..." />
                    </div>

                    <div class="wiz__people-bar">
                      <span>{{ filteredUsers.length }} {{ filteredUsers.length === 1 ? 'person' : 'people' }}</span>
                      <button v-if="filteredUsers.length > 0" @click="selectAllFiltered">{{ allFilteredSelected ? 'Deselect all' : 'Select all' }}</button>
                    </div>

                    <div class="wiz__people-list">
                      <div v-for="(user, index) in filteredUsers" :key="index" class="wiz__person" :class="{ 'wiz__person--selected': selectedUsers.includes(user.id) }" @click="toggleUserSelection(user)">
                        <div class="wiz__person-check">
                          <i v-if="selectedUsers.includes(user.id)" class="feather icon-check"></i>
                        </div>
                        <div class="wiz__person-info">
                          <p class="wiz__person-name">{{ user.name }}</p>
                          <div class="wiz__person-meta">
                            <span v-if="user.role" class="wiz__person-role">{{ user.role }}</span>
                            <span class="wiz__person-email">{{ user.email }}</span>
                          </div>
                        </div>
                      </div>
                      <div v-if="filteredUsers.length === 0" class="wiz__people-empty">
                        <i class="feather icon-search"></i>
                        <p>{{ $t('orgDashboard.users.noMatches') }}</p>
                      </div>
                    </div>
                  </div>
                </transition>
              </div>
            </template>

            <!-- Payment section -->
            <transition name="cfg-expand">
              <div v-if="showPaymentStep" class="wiz__payment">
                <div class="wiz__payment-header">
                  <i class="feather icon-credit-card"></i>
                  <span>{{ $t('orgDashboard.users.paymentdetails') }}</span>
                </div>
                <div id="card-element" class="wiz__stripe-card"></div>
                <p v-if="paymentError" class="wiz__payment-error">
                  <i class="feather icon-alert-circle"></i> {{ paymentError }}
                </p>
              </div>
            </transition>
            
          </div> <!-- END of wiz__step-scrollable wrapper -->
        </div>

          <!-- Wizard Footer -->
          <div class="wiz__footer">
            <button v-if="wizardStep > 1 && !isResumePayment && !showPaymentStep" class="wiz__back" @click="wizardStep--">
              <i class="feather icon-arrow-left"></i> {{ $t('orgDashboard.users.back') }}
            </button>
            <div v-else></div>

            <button class="wiz__next" :class="{ 'wiz__next--ready': wizardStep === 3 }" @click="handleWizardNext" :disabled="!canAdvanceWizard || isLoading">
              <template v-if="isLoading">
                <i class="feather icon-loader fa-spin"></i> {{ loadingStatusText }}
              </template>
              <template v-else-if="wizardStep === 1">
                {{ $t('orgDashboard.users.design') }} <i class="feather icon-arrow-right"></i>
              </template>
              <template v-else-if="wizardStep === 2">
                {{ $t('orgDashboard.users.looksgood') }} <i class="feather icon-arrow-right"></i>
              </template>
              <template v-else-if="wizardStep === 3">
                <template v-if="isCurrentPlanFree">
                  {{ $t('orgDashboard.users.launchplan') }} <i class="feather icon-arrow-right"></i>
                </template>
                <template v-else>
                  <!-- THE FIX: Change text if Stripe is visible -->
                  <template v-if="showPaymentStep">
                    {{ $t('orgDashboard.users.pay') }} <i class="feather icon-check"></i>
                  </template>
                  <template v-else>
                    {{ $t('orgDashboard.users.continue') }} <i class="feather icon-arrow-right"></i>
                  </template>
                </template>
              </template>
            </button>
            <SuccessionPlanWizard 
              :active.sync="showSuccessionWizard" 
              :orgData="orgData" 
              :isAddingCredits="isAddingCredits"
              :targetPlan="targetPlan"
              @plans-created="onPlansCreated"
            />
          </div>
      </template>
      </div>
  </vs-popup>

  <!-- ⭐️ DEDICATED ADD CREDITS MODAL (STYLED LIKE WIZARD STEP 4) ⭐️ -->
<vs-popup :title="' '" :active.sync="showAddCreditsModal" class="configure-popup succession-wizard">
  <div class="cfg">
    <button class="cfg__close-btn" @click="showAddCreditsModal = false">
      <i class="feather icon-x"></i>
    </button>

    <!-- Progress Dots matching step 4 and 5 -->
    <div class="wiz__dots" v-if="!addCreditsPlan || !addCreditsPlan.isFree">
      <div class="wiz__dot" :class="{ 'wiz__dot--done': addCreditsStep > 1, 'wiz__dot--current': addCreditsStep === 1 }">
        <span><i v-if="addCreditsStep > 1" class="feather icon-check"></i><template v-else>1</template></span>
        <p>{{ addCreditsPlan && addCreditsPlan.isCoachPlan ? 'Credits' : 'Limits' }}</p>
      </div>
      <div class="wiz__dot-line" :class="{ 'wiz__dot-line--active': addCreditsStep >= 2 }"></div>
      
      <div class="wiz__dot" :class="{ 'wiz__dot--current': addCreditsStep === 2 }">
        <span>2</span>
        <p>Launch</p>
      </div>
    </div>

    <div class="wiz__step-scrollable">
      
      <!-- STEP 1: Select Credits (Matches Image 2 exactly) -->
      <div v-if="addCreditsStep === 1" class="wiz__step">
        <div class="wiz__hero">
            <h3 class="wiz__title">Increase Participants</h3>
            <p class="wiz__subtitle">Select how many additional {{ addCreditsPlan && addCreditsPlan.isCoachPlan ? 'credits' : 'participants' }} you want to add to <strong>{{ targetPlanTitle }}</strong>.</p>
        </div>
        
        <div class="credit-list">
          <div class="credit-row">
            <span class="fw-name">
              <i :class="'feather ' + targetPlanIcon"></i> {{ targetPlanTitle }}
            </span>
            <div class="wiz__size-control">
              <button class="wiz__size-btn" @click="addCreditsAmount = Math.max(1, addCreditsAmount - 1)">
                <i class="feather icon-minus"></i>
              </button>
              <input type="number" class="wiz__size-input" v-model.number="addCreditsAmount" min="1" />
              <button class="wiz__size-btn" @click="addCreditsAmount++">
                <i class="feather icon-plus"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- STEP 2: Checkout -->
      <div v-if="addCreditsStep === 2" class="wiz__step">
        <div class="wiz__hero">
          <h3 class="wiz__title">Review & Pay</h3>
          <p class="wiz__subtitle">Review your succession coaching plan details.</p>
        </div>
        <div class="wiz__summary">
          <h4 class="wiz__summary-title">Order Summary</h4>
          <div class="wiz__summary-row">
            <span><i class="feather icon-briefcase"></i> {{ orgData.name || 'Your Organization' }}</span>
          </div>
          <div class="wiz__summary-divider"></div>
          
          <div class="wiz__summary-row">
            <span>
              {{ targetPlanTitle }} Upgrade 
              <span v-if="!addCreditsPlan || !addCreditsPlan.isFree">(&euro;{{ targetPlanPrice }}/user)</span>
            </span>
            <span>{{ addCreditsAmount }} additional {{ addCreditsPlan && addCreditsPlan.isCoachPlan ? 'credits' : 'participants' }}</span>
          </div>
          <div class="wiz__summary-divider"></div>
          
          <div class="wiz__summary-row wiz__summary-row--total" v-if="!addCreditsPlan || !addCreditsPlan.isFree">
            <span>Total Upgrade Cost</span>
            <span class="wiz__pricing-total">&euro;{{ (addCreditsAmount * targetPlanPrice).toFixed(2) }}</span>
          </div>
          
          <div class="wiz__summary-row wiz__summary-row--total" v-else>
            <span>Total Cost</span>
            <span class="wiz__pricing-total" style="color: #059669;">FREE</span>
          </div>
        </div>

        <div class="wiz__payment">
          <div class="wiz__payment-header">
            <i class="feather icon-credit-card"></i>
            <span>Payment Details</span>
          </div>
          <div class="wiz__stripe-card">
            <div class="dummy-card-element">
               <span class="dummy-placeholder">Card number</span>
               <div class="dummy-right">
                  <span>MM/YY</span>
                  <span>CVC</span>
               </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Actions (Matches Image 2 exactly) -->
    <div class="wiz__footer">
      <button v-if="addCreditsStep > 1" class="wiz__back" @click="addCreditsStep--">
        <i class="feather icon-arrow-left"></i> Back
      </button>
      <div v-else></div> <!-- Empty div to keep the right button pushed to the edge -->

      <!-- Dynamic button logic based on the step -->
      <button class="wiz__next" :class="{ 'wiz__next--ready': addCreditsStep === 2 }" @click="handleAddCreditsNext" :disabled="addCreditsLoading">
        <template v-if="addCreditsLoading">
          <i class="feather icon-loader fa-spin"></i> Processing...
        </template>
        
        <!-- STEP 1: Continue -->
        <template v-else-if="addCreditsStep === 1">
          {{ addCreditsPlan && addCreditsPlan.isFree ? 'Update Limits' : 'Continue' }} <i class="feather" :class="addCreditsPlan && addCreditsPlan.isFree ? 'icon-check' : 'icon-arrow-right'"></i>
        </template>
        
        <!-- STEP 2: Pay & Upgrade -->
        <template v-else>
          Pay & Upgrade <i class="feather icon-check"></i>
        </template>
      </button>
    </div>
  </div>
</vs-popup>
<vs-prompt 
      color="danger" 
      class="deactivate-board-prompt" 
      title="Confirm Board Deactivation"
      @cancel="deactivationText = null" 
      @accept="deactivateBoard" 
      @close="deactivationText = null"
      accept-text="Deactivate" 
      :is-valid="deactivationText === boardTitle" 
      :active.sync="deactivationConfirmPrompt">
      <div class="mb-4">
        <h5>You are about to deactivate this board.</h5>
        <br />
        <p>After a board is deactivated, it <b>cannot be recovered</b>.</p>
        <br />
        <p class="mb-2">Please type the following to confirm:</p>
        <i style="background: lightgray; padding: 5px; border-radius: 5px; display: inline-block;">{{ boardTitle }}</i><br />
      </div>
      <vs-input class="w-full mt-3" name="Deactivate Board" v-model="deactivationText" placeholder="Type plan title to confirm" />
    </vs-prompt>

  </div>
</template>

<script>
import axios from 'axios';
import SuccessionPlanWizard from './SuccessionPlanWizard.vue';
export default {
  name: 'UserRecommendations',
  components: {
    SuccessionPlanWizard // <--- ADD THIS
  },
  data() {
    return {
      viewMode: 'detailed',

      selectedIcon: '',
      playingVideo: null,
      showGuidesModal: false,
      showContactModal: false,
      guideVideos: [
        { id: 'vIfbIkeNLK8', title: 'How to Create Your First Plan', desc: 'Set up a fitment, development or succession plan and invite participants', duration: '3:45' },
        { id: 'VIDEO_ID_2', title: 'Managing Your Talent Pool', desc: 'Add people, assign roles and track progress across your organization', duration: '5:12' },
        { id: 'VIDEO_ID_3', title: 'Understanding KVDR Assessments', desc: 'Learn how competency, strategy, execution and culture scores drive recommendations', duration: '4:30' },
      ],

      isCoachPlan: false,
      maxCredits: 0,
      showSuccessionWizard: false,
      
      isModalActive: false,
      isEditMode: false,
      isLoading: false,
      loadingStatusText: 'Processing...',

      selectedPlanName: '',
      selectedPlanId: null,
      activePlans: [],
      pendingPlans: [],
      hasPlansCache: null,   
      plansFetched: false,   
      allMongoBoards: [],
      boardTitle: '',
      
      userSearchQuery: '',
      selectedUsers: [], 
      activeRoleFilters: [],
      showRoleDropdown: false, 
      
      usersList: [], 
      orgDesignRoles: [], 
      
      editingBoardId: null,
      originalBoardMembers: [],
      isLoadingBoardDetails: false,

      planAppsMap: {
        'Fitment Plan': [],
        'Development Plan': ['alex'], 
        'Succession Plan': ['notes'] 
      },
      defaultApps: [],
      roleColorsCache: {},
      showPlanOptionsDropdown: false,
      deactivationConfirmPrompt: false,
      deactivationText: null,

      // AI Wizard
      wizardStep: 1,
      aiUseCase: '',
      aiGenerating: false,
      aiGeneratedCols: [],
      wizardTeamSize: 10,
      showParticipantPicker: false,
      isResumePayment: false,

      // Payment
      showPaymentStep: false,
      pendingBoardId: null,
      pendingSubscriptionId: null,
      pendingStripePaymentIntentId: null,
      stripeInstance: null,
      stripeCardElement: null,
      paymentError: null,
      isAddingCredits: false,
      targetPlan: null,
      selectedPlanObject: null,
      showAddCreditsModal: false,
      addCreditsStep: 1,
      addCreditsAmount: 5 ,
      addCreditsPlan: null,
      addCreditsLoading: false,
      editingBoardId: null,
      planLimit: 0, // <--- ADD THIS
      originalBoardMembers: [],
    }
  },
  computed: {
    orgData() { return this.$store.state.organization.orgData || {}; },
    userData() { return this.$store.state.users.data || {}; },

    plansState() {
      if (this.activePlans.length > 0 || this.pendingPlans.length > 0) return 'has-plans';
      if (this.plansFetched) return 'no-plans';
      if (this.hasPlansCache === true) return 'loading';
      if (this.hasPlansCache === false) return 'no-plans';
      return 'loading';
    },

    hasValidName() {
      return this.isEditMode || this.isFreeCreateMode || this.boardTitle.trim().length >= 3;
    },

    wizardProgressPercent() {
      return (this.wizardStep / 3) * 100;
    },

    isCurrentPlanFree() {
      return this.selectedPlanId === 'plan_hiring_001';
    },

    canAdvanceWizard() {
      if (this.wizardStep === 1) return this.aiUseCase.trim().length >= 10;
      if (this.wizardStep === 2) return this.boardTitle.trim().length >= 3 && this.aiGeneratedCols.length > 0 && !this.aiGenerating;
      if (this.wizardStep === 3) return this.wizardTeamSize >= 1;
      return false;
    },

    configStep() {
      if (!this.hasValidName) return 'name';
      if (this.selectedUsers.length === 0) return 'participants';
      return 'ready';
    },

    allFilteredSelected() {
      return this.filteredUsers.length > 0 && this.filteredUsers.every(u => this.selectedUsers.includes(u.id));
    },

    availableRoles() {
      let roles = this.orgDesignRoles;
      roles = roles.filter(role => {
        const r = String(role).toLowerCase();
        return r !== 'talent pool candidate' && r !== 'general' && r !== 'unassigned' && r !== 'undefined' && r.trim() !== '';
      });
      let finalRoles = [...new Set(roles)].sort();
      finalRoles.unshift('Unassigned'); 
      return finalRoles;
    },
    
    filteredUsers() {
      // Clone array to prevent mutating the original state
      let users = [...this.usersList];
      
      if (this.userSearchQuery) {
        const query = this.userSearchQuery.toLowerCase();
        users = users.filter(u => u.name.toLowerCase().includes(query) || u.email.toLowerCase().includes(query));
      }
      
      if (this.activeRoleFilters.length > 0) {
        users = users.filter(u => {
            const userRole = (u.role || 'Unassigned').toLowerCase();
            return this.activeRoleFilters.some(filter => userRole === filter.toLowerCase());
        });
      }

      // Sort users: Originally selected (saved) members float to the top
      users.sort((a, b) => {
        const aSelected = this.originalBoardMembers.includes(a.id) ? 1 : 0;
        const bSelected = this.originalBoardMembers.includes(b.id) ? 1 : 0;
        return bSelected - aSelected;
      });

      return users;
    },
    dynamicPlanTitle() {
      const name = this.selectedPlanName || ''; 

      if (name.includes('Fitment')) {
          return 'Onboarding Plan for Fitment';
      } else if (name.includes('Development')) {
          return 'Learning Plan for Development';
      } else if (name.includes('Succession')) {
          return 'Coaching Plan for Promotion';
      }

      return 'Setting up Plans'; 
    },
    remainingCredits() {
      // Use maxCredits for Coach, planLimit for standard boards
      const limit = this.isCoachPlan ? this.maxCredits : this.planLimit;
      if (!limit) return 999; 
      return limit - this.selectedUsers.length;
    },
    targetPlanTitle() {
      return this.addCreditsPlan ? this.addCreditsPlan.title : 'Coach Plan';
    },
    targetPlanFramework() {
      if (!this.addCreditsPlan) return 'behavioural';
      return this.addCreditsPlan.frameworkId || 'behavioural';
    },
    targetPlanPrice() {
      if (!this.addCreditsPlan) return 70;
      
      // Standard Paid Plans (Fitment, etc) cost 5 per user
      if (!this.addCreditsPlan.isCoachPlan) {
        return this.addCreditsPlan.isFree ? 0 : 5;
      }
      
      // Coach Plans dynamic pricing
      const prices = {
        'behavioural': 70,
        'founder': 90,
        'performance': 80,
        'ecosystem': 100
      };
      return prices[this.targetPlanFramework] || 70;
    },
    targetPlanIcon() {
      if (!this.addCreditsPlan) return 'icon-award';
      if (!this.addCreditsPlan.isCoachPlan) return 'icon-users'; // Default icon for standard plans
      
      const icons = {
        'behavioural': 'icon-users',
        'founder': 'icon-award',
        'performance': 'icon-trending-up',
        'ecosystem': 'icon-globe'
      };
      return icons[this.targetPlanFramework] || 'icon-award';
    }
  },
  watch: {
    orgData: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal._id) {
          this.initData(newVal._id);
        }
      }
    },
    showGuidesModal(val) {
      if (!val) this.playingVideo = null;
    },
    selectedUsers(val) {
      if (val.length > this.wizardTeamSize) {
        this.wizardTeamSize = val.length;
      }
    },
    showSuccessionWizard(val) {
      if (!val) {
        this.isAddingCredits = false;
        this.targetPlan = null;
      }
    }
  },
  methods: {
    getCacheKey() {
        return `gtbrdd_has_plans_${this.orgData._id}`;
    },

    openSuccessionWizard() {
      this.showSuccessionWizard = true;
    },

    loadCachedHasPlans() {
        try {
            const cached = localStorage.getItem(this.getCacheKey());
            if (cached !== null) {
                this.hasPlansCache = cached === 'true';
            }
        } catch (e) { /* ignore */ }
    },

    saveCachedHasPlans(hasPlans) {
        try {
            localStorage.setItem(this.getCacheKey(), String(hasPlans));
        } catch (e) { /* ignore */ }
    },

    async initData(orgId) {
        this.loadCachedHasPlans();
        this.fetchActivePlans();
        this.fetchOrgUsers(orgId);
        this.fetchRolesAndMapUsers(orgId);
    },

    goToEditBoardDetails() {
      this.showPlanOptionsDropdown = false;
      if (this.editingBoardId) {
        this.$router.push({ name: 'org-boards-edit', params: { boardId: this.editingBoardId } });
      }
    },

    async deactivateBoard() {
      if (!this.editingBoardId) return;
      this.$vs.loading();
      try {
        await this.$store.dispatch("boards/deactivateBoard", {
          boardId: this.editingBoardId,
          boardTitle: this.boardTitle,
          organizationId: this.orgData._id,
        });
        this.$vs.notify({
          title: "Board Deactivated",
          text: "The board has been deactivated.",
          color: "success"
        });
        this.deactivationConfirmPrompt = false;
        this.deactivationText = null;
        this.isModalActive = false;
        await this.fetchActivePlans();
      } catch (err) {
        console.error("Failed to deactivate board:", err);
        this.$vs.notify({
          title: "Error",
          text: "Failed to deactivate board.",
          color: "danger"
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    async fetchActivePlans() {
        if (!this.orgData || !this.orgData._id) return;

        try {
            const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
            const orgId = this.orgData._id;
            
            // --- 1. Fetch Mongo Boards ---
            const mongoUrl = `https://api.getboarded.tech/boards?organizationId=${orgId}`;
            const mongoResponse = await fetch(mongoUrl, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            const mongoRes = await mongoResponse.json();
            let allBoards = [];
            if (mongoRes && Array.isArray(mongoRes.data)) allBoards = mongoRes.data;
            else if (Array.isArray(mongoRes)) allBoards = mongoRes;
            else if (mongoRes && mongoRes.data && Array.isArray(mongoRes.data.data)) allBoards = mongoRes.data.data;
            this.allMongoBoards = allBoards;
            
            // --- 2. Fetch Python Coach Plans ---
            let coachPlans = [];
            const pyApiUrl = process.env.VUE_APP_API_URL || "http://localhost:8000";
            
            try {
                const pyResponse = await fetch(`${pyApiUrl}/api/v2/coach-plans/${orgId}`);
                if (pyResponse.ok) {
                    const pyRes = await pyResponse.json();
                    if (pyRes.data && Array.isArray(pyRes.data)) {
                        coachPlans = pyRes.data.map(plan => {
                            const planId = plan.id || plan.framework_id || plan._id;
                            const planTitle = plan.title || plan.plan_name || 'Coach Plan';
                            const planCredits = plan.total_credits || plan.numberOfUsers || 0;
                            const planMembers = Array.isArray(plan.assigned_users) ? plan.assigned_users : [];

                            return {
                                _id: planId, 
                                title: planTitle,
                                status: plan.status || 'active',
                                createdAt: plan.created_at || new Date().toISOString(),
                                numberOfUsers: planCredits,
                                members: planMembers,
                                isCoachPlan: true, 
                                maxCredits: planCredits,
                                frameworkId: plan.framework_id
                            };
                        });
                    }
                }
            } catch (pyErr) {
                console.warn("Could not fetch Coach Plans from Python DB:", pyErr);
            }

            // --- 3. Merge ---
            const normalize = str => (str || '').replace(/\s+/g, ' ').trim().toLowerCase();
            const coachPlanTitles = coachPlans.map(cp => normalize(cp.title));
            
            // Filter out Mongo boards that share the exact same name as a Coach Plan
            const deduplicatedMongoBoards = allBoards.filter(b => 
                !coachPlanTitles.includes(normalize(b.title))
            );

            const combinedPlans = [...deduplicatedMongoBoards, ...coachPlans];

            // ⭐️ 4. NEW: FETCH TALENT PLANS FROM PYTHON ⭐️
            try {
                const tRes = await fetch(`${pyApiUrl}/api/v2/talent-plans/org/${orgId}`);
                if (tRes.ok) {
                    const tJson = await tRes.json();
                    if (tJson.data) {
                        const talentPlansMap = {};
                        tJson.data.forEach(p => { talentPlansMap[p.board_id] = p; });
                        
                        combinedPlans.forEach(plan => {
                            if (!plan.isCoachPlan && talentPlansMap[plan._id]) {
                                const tData = talentPlansMap[plan._id];
                                plan.talentPlanLimit = tData.total_credits;
                                plan.talentAssignedUsers = tData.assigned_users;
                                plan.isFree = tData.is_free === 1;
                            }
                        });
                    }
                }
            } catch (e) { console.warn("Failed to fetch talent limits"); }

            combinedPlans.sort((a, b) => {
                const dateA = new Date(a.createdAt || 0);
                const dateB = new Date(b.createdAt || 0);
                return dateB - dateA;
            });

            const activePlansTemp = combinedPlans.filter(b => b.status === 'active');
            const pendingPlansTemp = combinedPlans.filter(b => b.status === 'pending_payment');

            // ⭐️ FIX: Use Promise.all to wait for exact counts before rendering ⭐️
            await Promise.all(activePlansTemp.map(async (plan) => {
                if (!plan.isCoachPlan) {
                    try {
                        const res = await this.$store.dispatch("boards/fetchUsersInBoard", plan._id);
                        const realMembers = res?.data?.data || res?.data || [];
                        plan.realMemberCount = realMembers.length;
                    } catch(e) { console.warn("Could not fetch real member count for plan:", plan.title); }
                }
            }));

            this.activePlans = activePlansTemp;
            this.pendingPlans = pendingPlansTemp;
            this.hasPlansCache = this.activePlans.length > 0 || this.pendingPlans.length > 0;
            this.saveCachedHasPlans(this.hasPlansCache);
            
        } catch (error) {
            console.error("Error fetching plans:", error);
        } finally {
            this.plansFetched = true;
        }
    },
    
    backToPlanList() {
        this.isModalActive = false;
    },

    async handleCoachBoardClick(plan) {
      const normalize = str => (str || '').replace(/\s+/g, ' ').trim().toLowerCase();
      const existingBoard = this.allMongoBoards.find(b => normalize(b.title) === normalize(plan.title));

      // --- HELPER 1: Generate Columns based on Plan Name ---
      const generateCols = () => {
        const getColBase = (title, description) => ({ title, description, colUsersData: [] });
        const planTitleLower = (plan.title || '').toLowerCase();

        if (planTitleLower.includes('behavioural')) {
          return [
            getColBase('Get Boarded', 'Onboarding to the platform'),
            getColBase('Awareness & Assessment', 'Initial self and 360 evaluation'),
            getColBase('Goal Definition', 'Setting behavioral targets'),
            getColBase('Active Coaching', 'Engaged in coaching sessions'),
            getColBase('Progress Review', 'Evaluating changes and feedback'),
            getColBase('Integration', 'Behavior successfully integrated')
          ];
        } else if (planTitleLower.includes('founder')) {
          return [
            getColBase('Get Boarded', 'Onboarding to the platform'),
            getColBase('Vision Alignment', 'Aligning personal and company goals'),
            getColBase('Leadership Scaling', 'Developing executive skills'),
            getColBase('Strategic Planning', 'Business strategy mapping'),
            getColBase('Execution & Milestones', 'Tracking business growth'),
            getColBase('Milestone Achieved', 'Successful execution')
          ];
        } else if (planTitleLower.includes('performance')) {
          return [
            getColBase('Get Boarded', 'Onboarding to the platform'),
            getColBase('KPI Setting', 'Defining clear metrics'),
            getColBase('Continuous Tracking', 'Ongoing performance logging'),
            getColBase('Mid-Cycle Review', 'Feedback and adjustments'),
            getColBase('Final Evaluation', 'End of cycle review'),
            getColBase('Completed', 'Evaluation closed')
          ];
        } else if (planTitleLower.includes('ecosystem')) {
          return [
            getColBase('Get Boarded', 'Onboarding to the platform'),
            getColBase('Network Mapping', 'Identifying key stakeholders'),
            getColBase('Engagement Strategy', 'Planning interactions'),
            getColBase('Relationship Building', 'Active networking'),
            getColBase('Value Creation', 'Mutual benefits realized'),
            getColBase('Sustained Ecosystem', 'Established network')
          ];
        } else {
          return [
            getColBase('Get Boarded', 'Onboarding to the platform'),
            getColBase('Talent Identification', 'Nominated for succession'),
            getColBase('Readiness Assessment', 'Skill and time gap analysis'),
            getColBase('Mentorship & Coaching', 'Active development'),
            getColBase('Shadowing', 'Role observation'),
            getColBase('Ready for Promotion', 'Fully prepared for the role')
          ];
        }
      };

      // --- HELPER 2: Add Users to Board (Strictly only assigned members) ---
      const populateUsers = async (boardId) => {
        if (plan.members && plan.members.length > 0) {
          for (const memberEmail of plan.members) {
            const matchedEmployee = this.usersList.find(u =>
              u.email === memberEmail || u.realUserId === memberEmail || u.id === memberEmail
            );
            if (matchedEmployee) {
              try {
                await this.$store.dispatch("boards/addUserToBoard", {
                  boardId: boardId, email: matchedEmployee.email, firstName: matchedEmployee.firstName, lastName: matchedEmployee.lastName
                });
              } catch(e) { console.warn("Participant add failed:", e); }
            }
          }
        }
      };

      // ==========================================
      // FLOW 1: BOARD ALREADY EXISTS (Check & Repair)
      // ==========================================
      if (existingBoard) {
        this.isLoading = true;
        this.$vs.loading({ text: 'Loading workspace...' });
        
        try {
          const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
          
          // 1. Fetch current Kanban columns to see if it's broken
          const kanbanRes = await fetch(`https://api-core-gateway.getboarded.com/boards/kanban/${existingBoard._id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const kanbanData = await kanbanRes.json();
          
          // 2. If it's a broken/empty board (0 columns), REPAIR IT!
          if (!kanbanData.data || !kanbanData.data.cols || kanbanData.data.cols.length === 0) {
             this.$vs.loading.change('Repairing empty board...');
             
             // A. Patch the boardView so it knows it is a Kanban board
             await fetch(`https://api-core-gateway.getboarded.com/boards/${existingBoard._id}`, {
               method: 'PATCH',
               headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
               body: JSON.stringify({ boardView: "kanban" })
             });

             // B. Inject the correct columns
             const kanbanCols = generateCols();
             await this.$store.dispatch("boards/createKanban", { boardId: existingBoard._id, cols: kanbanCols });
             
             // C. Wait for DB to settle
             await new Promise(resolve => setTimeout(resolve, 1500));
             
             // D. Add the users (Strictly only assigned ones)
             await populateUsers(existingBoard._id);
          }
        } catch (e) {
          console.warn("Board repair check failed, navigating anyway...", e);
        } finally {
          this.$vs.loading.close();
          this.isLoading = false;
        }

        return this.goToBoard(existingBoard);
      }

      // ==========================================
      // FLOW 2: COMPLETELY NEW BOARD
      // ==========================================
      this.isLoading = true;
      this.$vs.loading({ text: 'Setting up tracking board...' });

      try {
        const boardRes = await this.$store.dispatch("boards/createBoard", {
          title: plan.title,
          boardId: 'custom-kanban-board',
          userId: this.userData._id,
          organizationId: this.orgData._id,
          numberOfUsers: plan.numberOfUsers || 1,
          installedApps: [],
          paymentStatus: "paid",
          status: "active",
          boardView: "kanban", // Required to load Kanban UI
          slug: this.generateBoardSlug(plan.title)
        });

        const newBoardId = boardRes.data._id || (boardRes.data.data && boardRes.data.data._id);

        // Save columns
        const kanbanCols = generateCols();
        await this.$store.dispatch("boards/createKanban", { boardId: newBoardId, cols: kanbanCols });

        // Wait for DB to settle
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Add Users (Strictly only assigned ones)
        await populateUsers(newBoardId);

        // 👉 ADD THIS LINE: Refresh the plans list immediately
        await this.fetchActivePlans(); 

        // Update local state and navigate
        this.allMongoBoards.push({ _id: newBoardId, title: plan.title });
        this.$vs.loading.close();
        
        setTimeout(() => {
          this.$router.push(`/organization/${this.orgData.slug}/boards/${newBoardId}`);
        }, 500);
        
      } catch (error) {
        console.error("Error generating tracking board:", error);
        this.$vs.loading.close();
        this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not create tracking board. Check console.' });
      } finally {
        this.isLoading = false;
      }
    },

    async openEditModal(plan) {
      this.selectedPlanObject = plan; // <--- THIS FIXES THE WRONG PLAN NAME BUG
      this.isEditMode = true;
      this.isFreeCreateMode = false;
      this.isCoachPlan = false; 
      
      this.editingBoardId = plan._id;
      this.selectedPlanName = plan.title || ''; 
      this.boardTitle = plan.title || 'Workspace';
      this.planLimit = plan.talentPlanLimit || Number(plan.numberOfUsers); 
      
      this.selectedUsers = [];
      
      this.selectedUsers = [];
      this.originalBoardMembers = [];
      this.activeRoleFilters = [];
      this.showRoleDropdown = false;
      this.userSearchQuery = '';
      
      this.isModalActive = true;
      this.isLoadingBoardDetails = true;
      
      try {
          // 🟢 THE FIX: Use standard store action to get users reliably
          const res = await this.$store.dispatch("boards/fetchUsersInBoard", plan._id);
          const realMembers = res.data?.data || res.data || [];
          
          realMembers.forEach(member => {
              const userEmail = member.userData ? member.userData.email : (member.email || '');
              const userId = member.userData ? member.userData._id : (member.userId || member._id || '');
              const firstName = member.userData ? member.userData.firstName : (member.firstName || '');
              const lastName = member.userData ? member.userData.lastName : (member.lastName || '');
              
              let match = this.usersList.find(u => 
                  (u.email && userEmail && u.email.toLowerCase() === userEmail.toLowerCase()) || 
                  u.realUserId === userId || u.id === userId
              );

              // If invited via board email & not in org list, add them dynamically
              if(!match && userEmail) {
                  const newId = userId || userEmail;
                  match = {
                      id: newId,
                      realUserId: newId,
                      name: `${firstName} ${lastName}`.trim() || userEmail,
                      email: userEmail,
                      firstName: firstName,
                      lastName: lastName,
                      role: 'Invited via Board',
                      rawData: member
                  };
                  this.usersList.unshift(match); // Push to top
              }

              // Pre-check them
              if(match && !this.selectedUsers.includes(match.id)) {
                  this.selectedUsers.push(match.id);
                  this.originalBoardMembers.push(match.id);
              }
          });
      } catch(e) {
          console.error("Failed to load standard board users", e);
      } finally {
          this.isLoadingBoardDetails = false;
      }
    },

    openFreeActivationModal() {
      this.isFreeCreateMode = true; // Use the mode that skips AI steps
      this.isEditMode = false;
      this.isModalActive = true;
      this.boardTitle = 'Hiring Plan Workspace';
      this.selectedUsers = [];
      this.activeRoleFilters = [];
      this.userSearchQuery = '';
      this.isLoading = false;
    },

    openActivationModal(planName, planId, iconName) {
      this.isEditMode = false;
      this.isFreeCreateMode = false;
      this.editingBoardId = null;
      this.originalBoardMembers = [];
      this.selectedPlanName = planName;
      this.selectedPlanId = planId;
      this.selectedIcon = iconName;
      this.boardTitle = '';
      this.selectedUsers = [];
      this.activeRoleFilters = [];
      this.showPaymentStep = false;
      this.pendingBoardId = null;
      this.pendingSubscriptionId = null;
      this.pendingStripePaymentIntentId = null;
      this.paymentError = null;
      this.wizardStep = 1;
      this.aiUseCase = '';
      this.aiGenerating = false;
      this.aiGeneratedCols = [];
      this.wizardTeamSize = 10;
      this.showParticipantPicker = false;
      this.isResumePayment = false;
      this.isModalActive = true;
      this.isLoading = false;
      this.$store.dispatch("talentPlanPayments/resetPaymentState");
    },

    async confirmFreeActivation() {
      if (!this.boardTitle) return this.$vs.notify({ color: 'warning', text: 'Please enter a Plan Name.' });

      this.isLoading = true;
      this.loadingStatusText = "Setting up workspace...";

      try {
        // 1. Create the Board record
        const boardRes = await this.$store.dispatch("boards/createBoard", {
          title: this.boardTitle,
          boardId: 'custom-kanban-board',
          userId: this.userData._id,
          organizationId: this.orgData._id,
          numberOfUsers: this.selectedUsers.length || 1,
          installedApps: [], // No AI apps for free version
          paymentStatus: "paid", // Bypasses payment UI
          status: "active",      // Makes it immediately visible
          boardView: "kanban",
          slug: this.generateBoardSlug(this.boardTitle)
        });

        const newBoardId = boardRes.data._id;

        // 2. INITIALIZE COLUMNS (Fix for the empty board issue)
        // These titles match your Picture 4 exactly
        this.loadingStatusText = "Building columns...";
        const hiringCols = [
          { title: 'Initial Phase', description: 'New applicants', colUsersData: [] },
          { title: 'On Board', description: 'Screening & Interviews', colUsersData: [] },
          { title: 'Evaluating', description: 'Evaluation in progress', colUsersData: [] },
          { title: 'Completion', description: 'Hired / Closed', colUsersData: [] }
        ];

        // Crucial: We must await this so the board isn't empty when the page loads
        await this.$store.dispatch("boards/createKanban", {
          boardId: newBoardId,
          cols: hiringCols
        });

        // 3. Add Participants/Candidates if any were selected
        if (this.selectedUsers.length > 0) {
          this.loadingStatusText = "Adding candidates...";
          for (const userId of this.selectedUsers) {
            const user = this.usersList.find(u => u.id === userId);
            if (user) {
              await this.$store.dispatch("boards/addUserToBoard", {
                boardId: newBoardId,
                email: user.email,
                firstName: user.firstName,
                lastName: user.lastName
              });
            }
          }
        }

        // 👉 ADD THIS LINE: Refresh the plans list immediately
        await this.fetchActivePlans(); 

        this.loadingStatusText = "Ready!";
        this.$vs.notify({ color: 'success', title: 'Plan Created', text: 'Your hiring workspace is ready.' });
        
        // Close modal and redirect
        this.isModalActive = false;
        setTimeout(() => {
          this.$router.push(`/organization/${this.orgData.slug}/boards/${newBoardId}`);
        }, 500);

      } catch (error) {
        console.error("Board Creation Error:", error);
        this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not initialize board.' });
      } finally {
        this.isLoading = false;
      }
    },

    openCoachUrl() {
        window.open('https://coach.getboarded.com', '_blank');
    },

    // 2. FIX STANDARD EDIT MODAL (Must reset isCoachPlan to false!)
    async openEditModal(plan) {
      this.selectedPlanObject = plan;
      this.isEditMode = true;
      this.isFreeCreateMode = false;
      this.isCoachPlan = false; 
      
      this.editingBoardId = plan._id;
      this.selectedPlanName = plan.title || ''; 
      this.boardTitle = plan.title || 'Workspace';
      
      // Default fallback while loading
      this.planLimit = plan.talentPlanLimit || Number(plan.numberOfUsers) || 10; 
      
      this.selectedUsers = [];
      this.originalBoardMembers = [];
      this.activeRoleFilters = [];
      this.showRoleDropdown = false;
      this.userSearchQuery = '';
      
      this.isModalActive = true;
      this.isLoadingBoardDetails = true;
      
      try {
          // ⭐️ THE FIX: Fetch the exact, permanent limit directly from Python!
          const pyApiUrl = process.env.VUE_APP_API_URL || "http://localhost:8000";
          const limitRes = await fetch(`${pyApiUrl}/api/v2/talent-plans/board/${plan._id}`);
          if (limitRes.ok) {
              const limitData = await limitRes.json();
              if (limitData && limitData.data) {
                  // Hard overwrite with the absolute truth from the database (e.g., 11)
                  this.planLimit = limitData.data.total_credits; 
              }
          }

          // Fetch the users to populate the checkboxes
          const res = await this.$store.dispatch("boards/fetchUsersInBoard", plan._id);
          const realMembers = res?.data?.data || res?.data || [];
          
          realMembers.forEach(member => {
              const userObj = member.userData || member; 
              const userEmail = userObj.email || '';
              const userId = userObj._id || member.userId || member._id || '';
              const firstName = userObj.firstName || '';
              const lastName = userObj.lastName || '';
              
              let match = this.usersList.find(u => 
                  (u.email && userEmail && u.email.toLowerCase() === userEmail.toLowerCase()) || 
                  u.realUserId === userId || u.id === userId
              );

              if(!match && userEmail) {
                  const newId = userId || userEmail;
                  match = {
                      id: newId,
                      realUserId: newId,
                      name: `${firstName} ${lastName}`.trim() || userEmail,
                      email: userEmail,
                      firstName: firstName,
                      lastName: lastName,
                      role: 'Invited via Board',
                      rawData: member
                  };
                  this.usersList.unshift(match); 
              }

              if(match && !this.selectedUsers.includes(match.id)) {
                  this.selectedUsers.push(match.id);
                  this.originalBoardMembers.push(match.id);
              }
          });
      } catch(e) {
          console.error("Failed to load standard board users", e);
      } finally {
          this.isLoadingBoardDetails = false;
      }
    },

    // 3. SAFE ACTIVATION METHOD
    async confirmActivation() {
      this.isLoading = true;
      this.loadingStatusText = "Updating members...";
      
      // =========================================================
      // 1. COACH PLAN SAVE LOGIC
      // =========================================================
      if (this.isEditMode && this.isCoachPlan) {
        try {
          const pyApiUrl = process.env.VUE_APP_API_URL || "http://localhost:8000";
          const feathersApiUrl = 'https://api.coach.getboarded.com'; 
          
          const usersToSync = this.usersList.filter(u => this.selectedUsers.includes(u.id));
          
          // Send Mongo IDs to Python, NOT Emails!
          const mongoIdsToSaveInPython = [];
          for (const user of usersToSync) {
            mongoIdsToSaveInPython.push(user.realUserId || user.id); 
          }

          // Fetch Company ID from MySQL using mongo_org_id
          let mysqlCompanyId = null;
          const mongoOrgId = this.orgData ? this.orgData._id : null;
          if (mongoOrgId) {
             try {
                const compRes = await fetch(`${feathersApiUrl}/company?mongo_org_id=${mongoOrgId}`);
                const compData = await compRes.json();
                if (compData.data && compData.data.length > 0) {
                    mysqlCompanyId = compData.data[0].id;
                }
             } catch (e) { console.error("MySQL company ID fetch error", e); }
          }

          // Smart PATCH for syncing users to MySQL
          for (const user of usersToSync) {
            const raw = user.rawData || {};
            const emailToSync = user.email.toLowerCase();
            
            const mysqlPayload = {
              username: emailToSync.split('@')[0], 
              email: emailToSync, 
              password: Math.random().toString(36).slice(-10) + 'A1!',
              status: 'active', 
              user_type: 'trainee', 
              userType: 'trainee',
              first_name: user.firstName || user.name.split(' ')[0] || 'User', 
              firstName: user.firstName || user.name.split(' ')[0] || 'User',
              last_name: user.lastName || '', 
              lastName: user.lastName || '',
              job_title: user.role || null, 
              jobTitle: user.role || null,
              isVerified: 1, 
              flag: 'sso'
            };

            try {
                const checkRes = await fetch(`${feathersApiUrl}/users?email=${emailToSync}`);
                const checkData = await checkRes.json();
                
                if (checkData.data && checkData.data.length > 0) {
                  // USER EXISTS -> PATCH 
                  const existingUser = checkData.data[0];
                  const patchPayload = { ...mysqlPayload };
                  
                  // ⭐️ THE FIX: FORCE OVERWRITE COMPANY IF IT DOES NOT MATCH
                  if (mysqlCompanyId && existingUser.companyId !== mysqlCompanyId) {
                      patchPayload.companyId = mysqlCompanyId;
                      patchPayload.company_id = mysqlCompanyId;
                  }
                  
                  await fetch(`${feathersApiUrl}/users/${existingUser.id}`, { 
                      method: 'PATCH', 
                      headers: { 'Content-Type': 'application/json' }, 
                      body: JSON.stringify(patchPayload) 
                  });
                } else {
                  // NEW USER -> POST
                  const createPayload = {
                      ...mysqlPayload,
                      companyId: mysqlCompanyId,
                      company_id: mysqlCompanyId
                  };
                  await fetch(`${feathersApiUrl}/users`, { 
                      method: 'POST', 
                      headers: { 'Content-Type': 'application/json' }, 
                      body: JSON.stringify(createPayload) 
                  });
                }
            } catch (e) { console.error("Vue to MySQL sync failed", e); }
          }

          // Send Correct Mongo IDs to Python to sync the SQLite Plan!
          await fetch(`${pyApiUrl}/api/v2/coach-plans/${this.editingBoardId}/users`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_ids: mongoIdsToSaveInPython }) 
          });

          // =======================================================
          // ⭐️ THE CRITICAL FIX: GET THE CORRECT MONGO BOARD ID ⭐️
          // this.editingBoardId is the Python ID! We need the Mongo ID to update the UI board.
          // =======================================================
          const normalize = str => (str || '').replace(/\s+/g, ' ').trim().toLowerCase();
          const existingMongoBoard = this.allMongoBoards.find(b => normalize(b.title) === normalize(this.boardTitle));
          const actualMongoBoardId = existingMongoBoard ? existingMongoBoard._id : null;

          if (actualMongoBoardId) {
              const usersToAdd = this.usersList.filter(u => this.selectedUsers.includes(u.id) && !this.originalBoardMembers.includes(u.id));
              const usersToRemove = this.originalBoardMembers.filter(id => !this.selectedUsers.includes(id));

              // 1. Add newly checked users to the visual board
              for (const user of usersToAdd) {
                  try {
                    await this.$store.dispatch("boards/addUserToBoard", {
                      boardId: actualMongoBoardId, email: user.email, firstName: user.firstName, lastName: user.lastName
                    });
                  } catch(e) { console.warn("Failed to add to Mongo board", e); }
              }

              // 2. Remove unchecked users from the visual board
              for (const userId of usersToRemove) {
                  try {
                      const user = this.usersList.find(u => u.id === userId);
                      const userIdToRemove = user ? (user.realUserId || user.id) : userId;
                      await fetch(`${pyApiUrl}/api/v2/boards/${actualMongoBoardId}/users/${userIdToRemove}`, {
                          method: 'DELETE', headers: { 'Content-Type': 'application/json' }
                      });
                  } catch(e) {}
              }
          } else {
              console.warn("Could not find matching Mongo Board for Coach Plan:", this.boardTitle);
          }
          // =======================================================

          this.$vs.notify({ color: 'success', title: 'Updated', text: `Users synced and assigned to plan successfully.` });
          this.isModalActive = false;
          await this.fetchActivePlans();
        } catch (err) {
          this.$vs.notify({ color: 'danger', text: 'Failed to sync users.' });
        } finally {
          this.isLoading = false;
        }
        return;
      }

      // =========================================================
      // 2. STANDARD PLAN SAVE LOGIC (Free & Fitment Plans)
      // =========================================================
      if (this.isEditMode && !this.isCoachPlan) {
          try {
            const pyApiUrl = process.env.VUE_APP_API_URL || "http://localhost:8000";
            
            const usersToAdd = this.usersList.filter(u => this.selectedUsers.includes(u.id) && !this.originalBoardMembers.includes(u.id));
            const usersToRemove = this.originalBoardMembers.filter(id => !this.selectedUsers.includes(id));

            // ADD USERS
            for (const user of usersToAdd) {
                try {
                  await this.$store.dispatch("boards/addUserToBoard", {
                    boardId: this.editingBoardId, email: user.email, firstName: user.firstName, lastName: user.lastName
                  });
                } catch(e) { console.warn("Failed to add user:", e); }
            }

            // ⭐️ FIX: REMOVE USERS FROM MONGO & PYTHON USING THE CORRECT ID ⭐️
            const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
            let currentBoardUsers = [];
            try {
                const boardUsersRes = await this.$store.dispatch("boards/fetchUsersInBoard", this.editingBoardId);
                currentBoardUsers = boardUsersRes?.data?.data || boardUsersRes?.data || [];
            } catch(e) { console.warn("Failed to fetch current board users for removal"); }

            for (const userId of usersToRemove) {
                try {
                    // Get the REAL User ID (not Employee ID) and email
                    const user = this.usersList.find(u => u.id === userId);
                    const realId = user ? (user.realUserId || user.id) : userId;
                    const userEmail = user ? user.email : null;

                    // 1. Delete from Python DB using the real ID
                    await fetch(`${pyApiUrl}/api/v2/boards/${this.editingBoardId}/users/${realId}`, {
                        method: 'DELETE', headers: { 'Content-Type': 'application/json' }
                    });

                    // 2. Find ALL matching board-user records in Mongo (cleans up duplicate ghost cards)
                    const boardUserRecords = currentBoardUsers.filter(member => {
                       const uid = (member.userData && member.userData._id) || member.userId || member._id;
                       const uemail = (member.userData && member.userData.email) || member.email;
                       
                       const matchById = realId && uid === realId;
                       const matchByEmail = userEmail && uemail && uemail.toLowerCase() === userEmail.toLowerCase();
                       
                       return matchById || matchByEmail;
                    });
                    
                    // 3. Force Delete from the visual board
                    for (const record of boardUserRecords) {
                        if (record && record._id) {
                            await fetch(`https://api.getboarded.tech/board-users/${record._id}`, {
                                method: 'DELETE', 
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                        }
                    }
                } catch(e) { console.warn("Error removing user", e); }
            }

            try {
                await fetch(`${pyApiUrl}/api/v2/boards/${this.editingBoardId}/sync-coach-plan-users`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' }
                });
            } catch(e) {}

            try {
                await axios.put(`${pyApiUrl}/api/v2/talent-plans/board/${this.editingBoardId}/sync`, {
                    assigned_users: this.selectedUsers
                });
            } catch(e) { console.warn("Failed to sync talent plan users", e); }

            this.$vs.notify({ color: 'success', title: 'Updated', text: `Plan members updated successfully.` });
            this.isModalActive = false;
            
            // Instantly update UI counter to prevent it from bouncing back
            const uiPlan = this.activePlans.find(p => p._id === this.editingBoardId);
            if (uiPlan) {
                this.$set(uiPlan, 'realMemberCount', this.selectedUsers.length);
            }

            // Small delay to ensure DB deletions commit before re-fetching
            setTimeout(async () => {
                await this.fetchActivePlans(); 
            }, 800);
            
          } catch(err) {
              this.$vs.notify({ color: 'danger', text: 'Update failed' });
          } finally {
              this.isLoading = false;
          }
      }
    },

    // 3. Strictly block adding users over the limit
    

    async launchWizardPlan() {
      if (!this.boardTitle) return this.$vs.notify({ color: 'warning', text: 'Please enter a plan name.' });
      if (this.wizardTeamSize < 1) return this.$vs.notify({ color: 'warning', text: 'Set at least 1 participant.' });

      this.isLoading = true;

      // FIX 1: Generate unique IDs for Kanban frontend rendering requirements
      let kanbanCols = this.aiGeneratedCols.map((col, index) => ({
        id: `col_${Math.random().toString(36).substr(2, 9)}_${index}`, 
        title: col.title || `Stage ${index + 1}`,
        description: col.description || '',
        colUsersData: [],
        tasks: []
      }));

      if (kanbanCols.length === 0) {
        kanbanCols = [
          { id: 'col_1', title: 'To Do', description: 'Pending Tasks', colUsersData: [], tasks: [] },
          { id: 'col_2', title: 'In Progress', description: 'Currently working on', colUsersData: [], tasks: [] },
          { id: 'col_3', title: 'Done', description: 'Completed', colUsersData: [], tasks: [] }
        ];
      }

      // ==========================================
      // FLOW 1: FREE PLAN
      // ==========================================
      if (this.isCurrentPlanFree) {
        this.loadingStatusText = "Setting up your workspace...";
        try {
          const boardRes = await this.$store.dispatch("boards/createBoard", {
            title: this.boardTitle,
            boardId: 'custom-kanban-board',
            userId: this.userData._id,
            organizationId: this.orgData._id,
            numberOfUsers: this.wizardTeamSize,
            installedApps: [],
            paymentStatus: "paid", 
            status: "active",      
            slug: this.generateBoardSlug(this.boardTitle)
          });
          
          // FIX 2: Safely extract ID whether backend returns res.data._id OR res.data.data._id
          const newBoardId = boardRes.data._id || (boardRes.data.data && boardRes.data.data._id);
          localStorage.setItem('board_capacity_' + newBoardId, this.wizardTeamSize);

          this.loadingStatusText = "Building your board...";

          
          if (!newBoardId) {
             console.error("Board Response:", boardRes);
             throw new Error("Backend did not return a valid Board ID");
          }

          this.loadingStatusText = "Building your board...";

          await this.$store.dispatch("boards/createKanban", { 
            boardId: newBoardId, 
            cols: kanbanCols 
          });

          // Add users if they were selected in the wizard
          if (this.selectedUsers && this.selectedUsers.length > 0) {
            this.loadingStatusText = "Adding participants...";
            const usersToInvite = this.usersList.filter(u => this.selectedUsers.includes(u.id));
            for (const user of usersToInvite) {
              if (user.email === this.userData.email) continue; 
              try {
                await this.$store.dispatch("boards/addUserToBoard", {
                  boardId: newBoardId, email: user.email, firstName: user.firstName, lastName: user.lastName
                });
              } catch(e) { console.warn(e); }
            }
          }

          // 👉 ADD THIS LINE: Refresh the plans list immediately
          await this.fetchActivePlans();

          this.$vs.notify({ color: 'success', title: 'Workspace Created!', text: 'Your free hiring plan is ready.' });
          setTimeout(() => this.$router.push(`/organization/${this.orgData.slug}/boards/${newBoardId}`), 800);
        } catch (error) {
          console.error("Board Creation Error:", error);
          this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not create the workspace. Check console.' });
        } finally {
          this.isLoading = false;
        }
        return;
      }

      // ==========================================
      // FLOW 2: PAID PLANS
      // ==========================================

      if (this.showPaymentStep) {
        try {
          this.loadingStatusText = "Processing payment...";
          this.paymentError = null;

          if (!this.stripeInstance || !this.stripeCardElement) {
            throw new Error("Payment gateway failed to load. Please check your ad-blocker.");
          }

          const { error } = await this.stripeInstance.confirmCardPayment(
            this.$store.state.talentPlanPayments.clientSecret,
            { payment_method: { card: this.stripeCardElement } }
          );

          if (error) { throw new Error(error.message); }

          this.loadingStatusText = "Activating plan...";
          await this.$store.dispatch("talentPlanPayments/confirmPayment", { subscriptionId: this.pendingSubscriptionId });
          
          if (this.selectedUsers && this.selectedUsers.length > 0) {
            this.loadingStatusText = "Adding participants...";
            const usersToInvite = this.usersList.filter(u => this.selectedUsers.includes(u.id));
            for (const user of usersToInvite) {
              if (user.email === this.userData.email) continue;
              try {
                await this.$store.dispatch("boards/addUserToBoard", {
                  boardId: this.pendingBoardId, email: user.email, firstName: user.firstName, lastName: user.lastName
                });
              } catch(e) { console.warn("Participant add failed:", e); }
            }
          }

          this.$vs.notify({ color: 'success', title: 'Payment Successful!', text: 'Your plan has been activated.' });
          setTimeout(() => this.$router.push(`/organization/${this.orgData.slug}/boards/${this.pendingBoardId}`), 800);
        } catch (error) {
          console.error("Payment error:", error);
          this.paymentError = error.message || "Payment failed. Please check your card details.";
        } finally {
          this.isLoading = false;
        }
        return;
      }

      try {
        this.loadingStatusText = "Preparing secure payment...";
        
        const appsToInstall = this.planAppsMap[this.selectedPlanName] || this.defaultApps;
        const boardRes = await this.$store.dispatch("boards/createBoard", {
          title: this.boardTitle,
          boardId: 'custom-kanban-board',
          userId: this.userData._id,
          organizationId: this.orgData._id,
          numberOfUsers: this.wizardTeamSize,
          installedApps: appsToInstall,
          paymentStatus: "pending",
          status: "pending_payment",
          slug: this.generateBoardSlug(this.boardTitle)
        });
        
        // FIX 3: Apply the same safe ID extraction to the Paid flow
        this.pendingBoardId = boardRes.data._id || (boardRes.data.data && boardRes.data.data._id);

        if (!this.pendingBoardId) {
            throw new Error("Backend did not return a valid pending Board ID");
        }

        await this.$store.dispatch("boards/createKanban", { 
          boardId: this.pendingBoardId, 
          cols: kanbanCols 
        });

        const subRes = await this.$store.dispatch("talentPlanPayments/createSubscription", {
          planType: "fitment",
          planName: this.boardTitle,
          boardId: this.pendingBoardId,
          organizationId: this.orgData._id,
          userId: this.userData._id,
          numberOfUsers: this.wizardTeamSize,
          userEmail: this.userData.email,
        });

        this.pendingSubscriptionId = subRes.data.subscriptionId;

        // 👉 ADD THIS LINE: Updates the background list to show the Pending Plan
        await this.fetchActivePlans();

        this.isModalActive = false;
        this.$nextTick(() => {
          this.isModalActive = true;
          this.showPaymentStep = true;
          this.isLoading = false;

          this.initStripe();
        });

      } catch (error) {
        console.error("Setup error:", error);
        this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not set up the payment. Check console.' });
        this.isLoading = false;
      }
    },

    // Utilities ...
    async fetchOrgUsers(orgId) {
      try {
        const res = await this.$store.dispatch("organization/getAllOrgEmployees", orgId);
        if (res && res.data) {
          this.usersList = res.data.map(employee => {
            let fname = '', lname = '', email = '';
            if (employee.userData) { 
                fname = employee.userData.firstName; 
                lname = employee.userData.lastName; 
                email = employee.userData.email; 
            }
            if (!fname) fname = employee.firstName; 
            if (!lname) lname = employee.lastName; 
            if (!email) email = employee.email;
            
            if ((!fname || !email) && employee.profileData?.personal_details) {
                const pd = employee.profileData.personal_details;
                fname = fname || pd.name?.firstName;
                lname = lname || pd.name?.lastName;
                if (!fname && pd.name?.fullName) { 
                    const parts = pd.name.fullName.split(' '); 
                    fname = parts[0]; 
                    lname = parts.slice(1).join(' '); 
                }
                email = email || pd.contactDetails?.emailId;
            }
            const finalName = (fname || lname) ? `${fname || ''} ${lname || ''}`.trim() : 'Unknown User';
            let realUserId = null;
            if (employee.userData && employee.userData._id) realUserId = employee.userData._id;
            else if (employee.userId) realUserId = employee.userId;

            let rawRole = employee.roleTitle || employee.designation || 'Unassigned';

            return { 
              id: employee._id,
              realUserId: realUserId,
              name: finalName, 
              email: email || '', 
              firstName: fname || '', 
              lastName: lname || '', 
              initials: this.getInitials(fname, lname), 
              role: this.toTitleCase(rawRole),
              rawData: employee.userData || employee
            };
          });
        }
      } catch (error) { console.error("Error fetching users:", error); }
    },

    async fetchRolesAndMapUsers(orgId) {
      try {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
        const step1Res = await fetch(`https://api.getboarded.tech/kvdr?orgId=${orgId}`, { method: 'GET', headers });
        const step1Data = await step1Res.json();
        
        let kvdrId = null;
        if (step1Data.data && Array.isArray(step1Data.data) && step1Data.data.length > 0) kvdrId = step1Data.data[0]._id;
        else if (step1Data.data && step1Data.data._id) kvdrId = step1Data.data._id;

        if (!kvdrId) return;

        const step2Res = await fetch(`https://api.getboarded.tech/kvdr-role?kvdrId=${kvdrId}`, { method: 'GET', headers });
        const step2Data = await step2Res.json();

        if (step2Data && step2Data.data && Array.isArray(step2Data.data)) {
            this.orgDesignRoles = step2Data.data.map(t => this.toTitleCase(t.title));
            const userIdToRoleMap = {};
            step2Data.data.forEach(roleObj => {
                const roleTitle = this.toTitleCase(roleObj.title);
                if (roleObj.kvdrRoleProfiles && Array.isArray(roleObj.kvdrRoleProfiles)) {
                    roleObj.kvdrRoleProfiles.forEach(profile => {
                        if (profile.userId) userIdToRoleMap[profile.userId] = roleTitle;
                    });
                }
            });

            this.usersList = this.usersList.map(user => {
                if (user.realUserId && userIdToRoleMap[user.realUserId]) {
                    user.role = userIdToRoleMap[user.realUserId];
                } else if (userIdToRoleMap[user.id]) {
                    user.role = userIdToRoleMap[user.id];
                }
                return user;
            });
        }
      } catch (error) { console.error("Fetch Error:", error); }
    },

    toTitleCase(str) {
      if (!str) return '';
      return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
    },
    getInitials(f, l) { if(!f&&!l)return'??'; const a=f?f[0]:''; const b=l?l[0]:''; return (a+b).toUpperCase(); },
    generateBoardSlug(t) { let r = (Math.random() + 1).toString(36).substring(3); return t.toLowerCase().replace(/ /g, "-").replace(/[^\w-]+/g, "") + "-" + r; },
    getRoleStyle(role) {
      const isSelected = this.activeRoleFilters.includes(role);
      if (isSelected) return { backgroundColor: '#FFC107', color: '#ffffff', borderColor: '#FFC107' };
      else return { backgroundColor: '#f1f5f9', color: '#475569', borderColor: 'transparent' };
    },

    async resumePayment(plan) {
      try {
        this.isLoading = true;
        this.$store.dispatch("talentPlanPayments/resetPaymentState");

        let subData;
        try {
          const res = await this.$store.dispatch("talentPlanPayments/resumePayment", plan._id);
          subData = res.data;
        } catch (resumeErr) {
          console.warn('Resume failed, creating new subscription:', resumeErr.message || resumeErr);
          const subRes = await this.$store.dispatch("talentPlanPayments/createSubscription", {
            planType: "fitment",
            planName: plan.title,
            boardId: plan._id,
            organizationId: this.orgData._id,
            userId: this.userData._id,
            numberOfUsers: plan.numberOfUsers || 10,
            userEmail: this.userData.email,
          });
          subData = subRes.data;
        }

        if (subData.alreadyPaid) {
          this.$vs.notify({ color: 'success', title: 'Already Paid', text: 'This plan has been activated.' });
          this.fetchActivePlans();
          this.isLoading = false;
          return;
        }

        this.isEditMode = false;
        this.isFreeCreateMode = false;
        this.isResumePayment = true;
        this.boardTitle = plan.title;
        this.pendingBoardId = plan._id;
        this.pendingSubscriptionId = subData.subscriptionId;
        this.wizardStep = 3;
        this.wizardTeamSize = plan.numberOfUsers || 10;
        this.aiGeneratedCols = [];
        this.selectedUsers = [];
        this.showParticipantPicker = false;
        this.showPaymentStep = true;
        this.isModalActive = true;
        this.isLoading = false;

        this.$nextTick(() => this.initStripe());
      } catch (err) {
        console.error(err);
        this.$vs.notify({ color: 'danger', text: err.message || 'Could not resume payment' });
        this.isLoading = false;
      }
    },

    async handleWizardNext() {
      if (this.wizardStep === 1) {
        this.wizardStep = 2;
        this.aiGenerating = true;
        try {
          const res = await this.$store.dispatch("boards/generateBoardPlan", { useCase: this.aiUseCase });
          if (res && res.data) {
            this.aiGeneratedCols = (res.data.steps || []).map(s => ({ title: s.title, description: s.description || '' }));
            if (!this.boardTitle.trim() && res.data.name) {
              this.boardTitle = res.data.name;
            }
          }
        } catch (err) {
          console.error('AI generation failed, using fallback:', err);
          this.aiGeneratedCols = this.generateColumnsFromUseCase(this.aiUseCase);
          if (!this.boardTitle.trim()) {
            this.boardTitle = this.suggestPlanName(this.aiUseCase);
          }
        }
        this.aiGenerating = false;
      } else if (this.wizardStep === 2) {
        this.wizardStep = 3;
      } else if (this.wizardStep === 3) {
        await this.launchWizardPlan();
      }
    },

    async launchWizardPlan() {
      if (!this.boardTitle) return this.$vs.notify({ color: 'warning', text: 'Please enter a plan name.' });
      if (this.wizardTeamSize < 1) return this.$vs.notify({ color: 'warning', text: 'Set at least 1 participant.' });

      this.isLoading = true;
      const pyApiUrl = process.env.VUE_APP_API_URL || "http://localhost:8000";

      // ==========================================
      // FLOW 1: FREE PLAN (Hiring Plan)
      // ==========================================
      if (this.isCurrentPlanFree) {
        this.loadingStatusText = "Setting up your workspace...";
        try {
          const boardRes = await this.$store.dispatch("boards/createBoard", {
            title: this.boardTitle,
            boardId: 'custom-kanban-board',
            userId: this.userData._id,
            organizationId: this.orgData._id,
            numberOfUsers: this.wizardTeamSize,
            installedApps: [],
            paymentStatus: "paid", // Bypasses payment
            status: "active",      // Launches immediately
            slug: this.generateBoardSlug(this.boardTitle)
          });
          
          // Safely extract ID
          const newBoardId = boardRes.data._id || (boardRes.data.data && boardRes.data.data._id);
          
          // ⭐️ NEW: Save to Python Talent Plans DB (Free)
          try {
            await axios.post(`${pyApiUrl}/api/v2/talent-plans`, {
              org_id: this.orgData._id,
              board_id: newBoardId,
              plan_name: this.boardTitle,
              is_free: true,
              total_credits: this.wizardTeamSize,
              assigned_users: this.selectedUsers 
            });
          } catch(e) { console.warn("Failed to save Talent limit", e); }

          this.loadingStatusText = "Building your board...";
          
          // Format columns exactly as the backend expects
          let kanbanCols = this.aiGeneratedCols.map(col => ({
            title: col.title,
            description: col.description || '',
            colUsersData: [] // <-- CRITICAL: Prevents backend rejection
          }));

          if (kanbanCols.length === 0) {
            kanbanCols = [
              { title: 'New Applicants', description: '', colUsersData: [] }, 
              { title: 'Screening', description: '', colUsersData: [] }, 
              { title: 'Interview', description: '', colUsersData: [] }, 
              { title: 'Offer', description: '', colUsersData: [] }, 
              { title: 'Hired', description: '', colUsersData: [] }
            ];
          }

          try {
            await this.$store.dispatch("boards/createKanban", { boardId: newBoardId, cols: kanbanCols });
          } catch(e) {
            console.error("Warning: Kanban creation issue", e);
          }

          // Add users if they were selected in the wizard
          if (this.selectedUsers && this.selectedUsers.length > 0) {
            this.loadingStatusText = "Adding participants...";
            const usersToInvite = this.usersList.filter(u => this.selectedUsers.includes(u.id));
            for (const user of usersToInvite) {
              if (user.email === this.userData.email) continue; 
              try {
                await this.$store.dispatch("boards/addUserToBoard", {
                  boardId: newBoardId, email: user.email, firstName: user.firstName, lastName: user.lastName
                });
              } catch(e) { console.warn(e); }
            }
          }

          this.$vs.notify({ color: 'success', title: 'Workspace Created!', text: 'Your free hiring plan is ready.' });
          setTimeout(() => this.$router.push(`/organization/${this.orgData.slug}/boards/${newBoardId}`), 800);
        } catch (error) {
          console.error(error);
          this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not create the workspace.' });
        } finally {
          this.isLoading = false;
        }
        return;
      }

      // ==========================================
      // FLOW 2: PAID PLANS (Fitment Plan, etc.)
      // ==========================================

      // PHASE 2: User has entered card details and clicks "Pay & Launch"
      if (this.showPaymentStep) {
        try {
          this.loadingStatusText = "Processing payment...";
          this.paymentError = null;

          if (!this.stripeInstance || !this.stripeCardElement) {
            throw new Error("Payment gateway failed to load. Please check your ad-blocker.");
          }

          const { error } = await this.stripeInstance.confirmCardPayment(
            this.$store.state.talentPlanPayments.clientSecret,
            { payment_method: { card: this.stripeCardElement } }
          );

          if (error) { throw new Error(error.message); }

          this.loadingStatusText = "Activating plan...";
          await this.$store.dispatch("talentPlanPayments/confirmPayment", { subscriptionId: this.pendingSubscriptionId });
          
          if (this.selectedUsers && this.selectedUsers.length > 0) {
            this.loadingStatusText = "Adding participants...";
            const usersToInvite = this.usersList.filter(u => this.selectedUsers.includes(u.id));
            for (const user of usersToInvite) {
              if (user.email === this.userData.email) continue;
              try {
                await this.$store.dispatch("boards/addUserToBoard", {
                  boardId: this.pendingBoardId, email: user.email, firstName: user.firstName, lastName: user.lastName
                });
              } catch(e) { console.warn(e); }
            }
          }

          await this.fetchActivePlans();

          this.$vs.notify({ color: 'success', title: 'Payment Successful!', text: 'Your plan has been activated.' });
          setTimeout(() => this.$router.push(`/organization/${this.orgData.slug}/boards/${this.pendingBoardId}`), 800);
        } catch (error) {
          console.error("Payment error:", error);
          this.paymentError = error.message || "Payment failed. Please check your card details.";
        } finally {
          this.isLoading = false;
        }
        return;
      }

      // PHASE 1: User clicks "Continue to Payment" -> Create pending board & get Stripe intent
      try {
        this.loadingStatusText = "Preparing secure payment...";
        
        const appsToInstall = this.planAppsMap[this.selectedPlanName] || this.defaultApps;
        const boardRes = await this.$store.dispatch("boards/createBoard", {
          title: this.boardTitle,
          boardId: 'custom-kanban-board',
          userId: this.userData._id,
          organizationId: this.orgData._id,
          numberOfUsers: this.wizardTeamSize,
          installedApps: appsToInstall,
          paymentStatus: "pending",
          status: "pending_payment",
          boardView: "kanban",
          slug: this.generateBoardSlug(this.boardTitle)
        });
        
        // Safely extract ID
        this.pendingBoardId = boardRes.data._id || (boardRes.data.data && boardRes.data.data._id);

        // ⭐️ NEW: Save to Python Talent Plans DB (Paid - Pending)
        try {
          await axios.post(`${pyApiUrl}/api/v2/talent-plans`, {
            org_id: this.orgData._id,
            board_id: this.pendingBoardId,
            plan_name: this.boardTitle,
            is_free: false,
            total_credits: this.wizardTeamSize,
            assigned_users: this.selectedUsers
          });
        } catch(e) { console.warn("Failed to save Talent limit", e); }

        let kanbanCols = this.aiGeneratedCols.map(col => ({
          title: col.title,
          description: col.description || '',
          colUsersData: [] 
        }));

        if (kanbanCols.length === 0) {
          kanbanCols = [
            { title: 'To Do', description: '', colUsersData: [] }, 
            { title: 'In Progress', description: '', colUsersData: [] }, 
            { title: 'Done', description: '', colUsersData: [] }
          ];
        }

        try {
          await this.$store.dispatch("boards/createKanban", { boardId: this.pendingBoardId, cols: kanbanCols });
        } catch(e) {
          console.error("Warning: Kanban creation issue", e);
        }

        const subRes = await this.$store.dispatch("talentPlanPayments/createSubscription", {
          planType: "fitment",
          planName: this.boardTitle,
          boardId: this.pendingBoardId,
          organizationId: this.orgData._id,
          userId: this.userData._id,
          numberOfUsers: this.wizardTeamSize,
          userEmail: this.userData.email,
        });

        this.pendingSubscriptionId = subRes.data.subscriptionId;

        this.isModalActive = false;
        this.$nextTick(() => {
          this.isModalActive = true;
          this.showPaymentStep = true;
          this.isLoading = false;
          this.initStripe();
        });

      } catch (error) {
        console.error("Setup error:", error);
        this.$vs.notify({ color: 'danger', title: 'Error', text: 'Could not set up the payment.' });
        this.isLoading = false;
      }
    },

    applyTemplate(type) {
      const templates = {
        onboarding: 'Onboard new hires with welcome orientation, skills assessment, mentor assignment, compliance training, and workspace setup',
        fitment: 'Evaluate candidates for role fitment with competency mapping, skill gap analysis, and development recommendations',
        review: 'Run a talent review cycle with self-assessment, manager evaluation, goal setting, and development planning',
        onboarding1: 'Online Interviews – stages for screening candidates remotely through phone or live video calls and tracking interview progress and feedback.',
        onboarding2: 'In-Person Interviews – stages for coordinating office interviews with managers and evaluating candidates through onsite discussions and assessments.',
        onboarding3: 'Hiring and Offer – stages covering final candidate selection, offer generation, and tracking offer acceptance in the recruitment process.'
      };
      
      if (templates[type]) {
        this.aiUseCase = templates[type];
      }
    },

    generateColumnsFromUseCase(useCase) {
      const lower = useCase.toLowerCase();
      const cols = [{ title: 'Welcome', description: 'Initial onboarding and orientation' }];

      if (lower.includes('assess') || lower.includes('skill') || lower.includes('competenc'))
        cols.push({ title: 'Skills Assessment', description: 'Evaluate core competencies and identify skill gaps' });
      if (lower.includes('mentor') || lower.includes('coach'))
        cols.push({ title: 'Mentor Assignment', description: 'Get matched with a mentor or coach for guidance' });
      if (lower.includes('compliance') || lower.includes('training') || lower.includes('learn'))
        cols.push({ title: 'Training & Compliance', description: 'Complete required training modules and certifications' });
      if (lower.includes('review') || lower.includes('evaluat') || lower.includes('performance'))
        cols.push({ title: 'Review', description: 'Performance review and manager feedback' });
      if (lower.includes('fitment') || lower.includes('role') || lower.includes('gap'))
        cols.push({ title: 'Role Fitment', description: 'Evaluate alignment with role requirements' });
      if (lower.includes('develop') || lower.includes('plan') || lower.includes('goal'))
        cols.push({ title: 'Development Plan', description: 'Create a personal growth and development roadmap' });
      if (lower.includes('environment') || lower.includes('setup') || lower.includes('tool'))
        cols.push({ title: 'Environment Setup', description: 'Set up tools, access, and workspace' });

      if (cols.length < 3) {
        cols.push({ title: 'In Progress', description: 'Active tasks and assignments' });
      }
      cols.push({ title: 'Complete', description: 'All steps finished successfully' });

      return cols;
    },

    suggestPlanName(useCase) {
      const lower = useCase.toLowerCase();
      if (lower.includes('onboard') || lower.includes('new hire') || lower.includes('joining'))
        return 'New Hire Onboarding';
      if (lower.includes('fitment') || lower.includes('role fit') || lower.includes('competenc'))
        return 'Role Fitment Assessment';
      if (lower.includes('review') || lower.includes('talent') || lower.includes('appraisal'))
        return 'Talent Review Cycle';
      if (lower.includes('training') || lower.includes('compliance') || lower.includes('learn'))
        return 'Training Program';
      if (lower.includes('develop') || lower.includes('growth') || lower.includes('skill'))
        return 'Development Plan';
      if (lower.includes('succession') || lower.includes('leadership'))
        return 'Succession Plan';
      const words = useCase.trim().split(/\s+/).slice(0, 4).join(' ');
      return words.charAt(0).toUpperCase() + words.slice(1);
    },

    closeConfigModal() {
      this.showPlanOptionsDropdown = false;
      this.isModalActive = false;
      this.isFreeCreateMode = false;
      this.showPaymentStep = false;
      if (this.stripeCardElement) {
        this.stripeCardElement.destroy();
        this.stripeCardElement = null;
      }
    },

    async initStripe() {
      try {
        const { loadStripe } = await import('@stripe/stripe-js');
        this.stripeInstance = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY);
        const elements = this.stripeInstance.elements();
        this.stripeCardElement = elements.create('card', {
          style: {
            base: {
              fontSize: '16px',
              color: '#111827',
              fontFamily: 'inherit',
              '::placeholder': { color: '#9CA3AF' },
            },
            invalid: { color: '#EF4444' },
          },
        });
        this.stripeCardElement.mount('#card-element');
        this.stripeCardElement.on('change', (event) => {
          this.paymentError = event.error ? event.error.message : null;
        });
      } catch (err) {
        console.error('Failed to init Stripe:', err);
        this.paymentError = 'Failed to load payment form. Please refresh and try again.';
      }
    },

    selectAllFiltered() {
      if (this.allFilteredSelected) {
        const ids = this.filteredUsers.map(u => u.id);
        this.selectedUsers = this.selectedUsers.filter(id => !ids.includes(id));
      } else {
        const ids = this.filteredUsers.map(u => u.id);
        this.selectedUsers = [...new Set([...this.selectedUsers, ...ids])];
      }
    },

    toggleUserSelection(user) {
      if (this.selectedUsers.includes(user.id)) {
        // Allow deselecting
        this.selectedUsers = this.selectedUsers.filter(id => id !== user.id);
      } else {
        // Check credit limit before selecting
        if (this.isCoachPlan && this.remainingCredits <= 0) {
          this.$vs.notify({ 
            color: 'warning', 
            title: 'Credit Limit Reached', 
            text: `You only have ${this.maxCredits} credits for this plan.` 
          });
          return;
        }
        this.selectedUsers.push(user.id);
      }
    },

    async openCoachPlanManager(plan) {
      this.selectedPlanObject = plan;
      this.isEditMode = true;
      this.isFreeCreateMode = false;
      this.isCoachPlan = true;
      
      this.editingBoardId = plan._id; 
      this.boardTitle = plan.title; 
      this.selectedPlanName = plan.title; 
      this.maxCredits = plan.maxCredits;
      
      this.selectedUsers = [];
      this.originalBoardMembers = [];
      this.activeRoleFilters = [];
      this.showRoleDropdown = false;
      this.userSearchQuery = '';
      
      this.isModalActive = true;
      this.isLoadingBoardDetails = true;

      try {
          // ALWAYS fetch fresh members data for this specific plan when opened
          const pyApiUrl = process.env.VUE_APP_API_URL;
          const pyResponse = await fetch(`${pyApiUrl}/api/v2/coach-plans/${this.orgData._id}`);
          if (pyResponse.ok) {
              const pyRes = await pyResponse.json();
              if (pyRes.data && Array.isArray(pyRes.data)) {
                  const freshPlan = pyRes.data.find(p => (p.id || p.framework_id || p._id) === plan._id);
                  if (freshPlan && Array.isArray(freshPlan.assigned_users)) {
                      plan.members = freshPlan.assigned_users;
                  }
              }
          }
      } catch (e) {
          console.warn("Could not fetch fresh coach plan data", e);
      }
      
      // THE FIX: Check against Email as well (because we saved emails to Python)
      if (plan.members && Array.isArray(plan.members)) {
          plan.members.forEach(memberIdentifier => {
              let matchedEmployee = this.usersList.find(u => 
                  u.email === memberIdentifier || 
                  u.realUserId === memberIdentifier || 
                  u.id === memberIdentifier
              );

              // 🟢 THE FIX: If email is in Python DB but not in local employee list
              if (!matchedEmployee && memberIdentifier.includes('@')) {
                  matchedEmployee = {
                      id: memberIdentifier,
                      realUserId: memberIdentifier,
                      name: memberIdentifier.split('@')[0], // Extracted name fallback
                      email: memberIdentifier,
                      role: 'Invited via Board',
                      rawData: {}
                  };
                  this.usersList.unshift(matchedEmployee); // Push to top
              }

              if (matchedEmployee) {
                  this.selectedUsers.push(matchedEmployee.id);
              }
          });
      }
      
      this.originalBoardMembers = [...this.selectedUsers];
      this.isLoadingBoardDetails = false;
      this.$forceUpdate();
    },
    triggerBuyCreditsFlow() {
      if (this.selectedPlanObject) {
        this.isModalActive = false; 
        
        // ⭐️ BUG FIX: Shallow copy prevents Vue Circular Reference crashes! ⭐️
        this.addCreditsPlan = { ...this.selectedPlanObject };
        this.addCreditsPlan.isCoachPlan = this.isCoachPlan; 
        
        this.addCreditsAmount = 5;
        this.addCreditsStep = 1;
        this.showAddCreditsModal = true;
      } else {
        console.warn("Could not find selectedPlanObject");
      }
    },

    async handleAddCreditsNext() {
      if (this.addCreditsStep === 1) {
        // Skip straight to processing if it's a Free Plan
        if (this.addCreditsPlan && this.addCreditsPlan.isFree) {
          await this.processCreditsUpgrade();
        } else {
          this.addCreditsStep = 2;
        }
      } else {
        await this.processCreditsUpgrade();
      }
    },

    async processCreditsUpgrade() {
      this.addCreditsLoading = true;
      try {
        const pyApiUrl = process.env.VUE_APP_API_URL || "http://localhost:8000";
        
        if (this.addCreditsPlan.isCoachPlan) {
          await axios.put(`${pyApiUrl}/api/v2/coach-plans/${this.addCreditsPlan._id}/add-credits`, {
            additional_credits: this.addCreditsAmount
          });
          this.maxCredits += this.addCreditsAmount;
        } else {
          // ⭐️ Pass fallback details so backend can create it if missing!
          await axios.put(`${pyApiUrl}/api/v2/talent-plans/board/${this.addCreditsPlan._id}/add-credits`, {
            additional_credits: this.addCreditsAmount,
            org_id: this.orgData._id,
            plan_name: this.addCreditsPlan.title || 'Workspace',
            is_free: !!this.addCreditsPlan.isFree,
            current_limit: this.planLimit || 10
          });
          this.planLimit += this.addCreditsAmount;
        }
        
        this.$vs.notify({ color: 'success', title: 'Success', text: `${this.addCreditsAmount} participants added to your plan limit!` });
        this.showAddCreditsModal = false;
        
        // Refresh the dashboard stats!
        await this.fetchActivePlans(); 
      } catch (e) {
        console.error("Failed to add limits:", e);
        this.$vs.notify({ color: 'danger', title: 'Error', text: 'Failed to process upgrade.' });
      } finally {
        this.addCreditsLoading = false;
      }
    },
    clearRoleFilters() {
      this.activeRoleFilters = [];
      this.showRoleDropdown = false;
    },

    toggleRoleGroup(role) {
      const usersInRole = this.usersList.filter(u => u.role.toLowerCase() === role.toLowerCase());
      const userIdsInRole = usersInRole.map(u => u.id);

      if (this.activeRoleFilters.includes(role)) {
        this.activeRoleFilters = this.activeRoleFilters.filter(r => r !== role);
        this.selectedUsers = this.selectedUsers.filter(id => !userIdsInRole.includes(id));
      } else {
        this.activeRoleFilters.push(role);
        userIdsInRole.forEach(id => {
          if (!this.selectedUsers.includes(id)) {
            this.selectedUsers.push(id);
          }
        });
      }
    },
    
    goToBoard(plan) {
        this.$router.push(`/organization/${this.orgData.slug}/boards/${plan._id}`);
    },
    formatDate(dateString) {
      if (!dateString) return 'Recently';
      const date = new Date(dateString);
      // Fallback for invalid dates
      if (isNaN(date.getTime())) return 'Recently';
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    
    getPlanThemeClass(plan) {
      const title = (plan.title || '').toLowerCase();
      
      if (title.includes('ecosystem') || title.includes('hiring') || title.includes('onboard') || title.includes('acquisition') || title.includes('interview')) return 'theme-hiring';
      if (title.includes('fitment') || title.includes('empanelment') || title.includes('fit')) return 'theme-fitment';
      if (title.includes('develop') || title.includes('learn') || title.includes('mentoring') || title.includes('training')) return 'theme-development';
      if (title.includes('succession') || title.includes('founder') || title.includes('promotion') || title.includes('review') || title.includes('cycle') || plan.isCoachPlan) return 'theme-succession';
      
      return 'theme-custom';
    },

    getPlanIcon(plan) {
      const title = (plan.title || '').toLowerCase();
      
      if (title.includes('ecosystem') || title.includes('hiring') || title.includes('onboard') || title.includes('acquisition') || title.includes('interview')) return 'icon-briefcase';
      if (title.includes('fitment') || title.includes('empanelment') || title.includes('fit')) return 'icon-star';
      if (title.includes('develop') || title.includes('learn') || title.includes('mentoring') || title.includes('training')) return 'icon-book-open';
      if (title.includes('succession') || title.includes('founder') || title.includes('promotion') || title.includes('review') || title.includes('cycle') || plan.isCoachPlan) return 'icon-award';
      
      return 'icon-layers';
    },

    // 👇 ADD THIS MISSING METHOD HERE 👇
    onPlansCreated() {
      this.fetchActivePlans();
    }
  } // <--- This bracket ends the "methods" block
} // <--- This bracket ends "export default"
</script>

<style lang="scss" scoped>
$brand: #FBBF15;
$brand-hover: #E8AE08;

.recommendations-container {
  padding: 0 3rem;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
}

/* ── Badges ── */
.plan-badge {
  display: inline-block;
  font-size: 0.6rem;
  font-weight: 800;
  padding: 2px 8px;
  border-radius: 50px;
  margin-left: 8px;
  vertical-align: middle;
  letter-spacing: 0.05em;
  transform: translateY(-2px);

  &--free {
    background: #D1FAE5;
    color: #059669;
  }

  &--paid {
    background: #FFEDD5;
    color: #C2410C;
  }
}

/* ── Header ── */
.rec-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2.5rem;

  &__title {
    font-size: 1.75rem;
    font-weight: 800;
    color: #111827;
    margin-bottom: 0.375rem;

    @media (max-width: 640px) {
      font-size: 1.35rem;
    }
  }

  &__subtitle {
    color: #6b7280;
    font-size: 1rem;

    @media (max-width: 640px) {
      font-size: 0.85rem;
    }
  }
}

.rec-header__actions {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.rec-header__help {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0.5rem 0.875rem;
  border-radius: 10px;
  border: 2px solid #e5e7eb;
  background: white;
  color: #6b7280;
  font-weight: 600;
  font-size: 0.825rem;
  cursor: pointer;
  transition: all 0.2s;

  i { font-size: 1rem; }

  &:hover {
    border-color: $brand;
    color: $brand;
    background: #FFFBEB;
    box-shadow: 0 0 0 3px rgba($brand, 0.1);
  }
}

.view-toggle-btn {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  border: 2px solid #e5e7eb;
  background: white;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  font-size: 1.2rem;

  &:hover {
    border-color: $brand;
    color: $brand;
    background: #FFFBEB;
    box-shadow: 0 0 0 3px rgba($brand, 0.1);
  }
}

/* ── Simple List View ── */
.plan-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2.5rem;

  &__item {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    padding: 1.25rem 1.5rem;
    background: white;
    border: 2px solid #f3f4f6;
    border-radius: 16px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: $brand;
      background: #FFFBEB;
      box-shadow: 0 4px 20px rgba($brand, 0.15);
      transform: translateX(4px);

      .plan-list__arrow {
        color: $brand;
        transform: translateX(4px);
      }

      .plan-list__icon {
        transform: scale(1.05);
      }
    }
  }

  &__icon {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: transparent !important; /* Removes the colored boxes */
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: transform 0.2s;

    i {
      color: #111827 !important; /* Forces font icons to be dark/black */
    }

    img {
      filter: grayscale(100%) brightness(0.1); /* Converts SVG app icons to black/white */
    }

    /* Override the old background gradients just in case */
    &--hiring, &--fitment, &--development, &--succession {
      background: transparent !important;
    }
  }

  &__info {
    flex: 1;
    min-width: 0;
    text-align: left;
  }

  &__name {
    font-size: 1.1rem;
    font-weight: 700;
    color: #111827;
    margin-bottom: 4px;
  }

  &__desc {
    font-size: 0.9rem;
    color: #9ca3af;
  }

  &__arrow {
    color: #d1d5db;
    font-size: 1.5rem;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }
}

/* ── Detailed Card View ── */
.plan-card {
  position: relative;
  background: white;
  border-radius: 8px; /* Sharper corners to match design */
  padding: 1.5rem;
  border: 1px solid #e5e7eb; /* Clean light border */
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  box-shadow: none;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05); /* Very subtle hover shadow */
  }

  &__icon {
    margin-bottom: 1rem;
    display: inline-flex;

    i {
      font-size: 1.25rem;
      color: #4b5563; /* Dark gray */
    }

    img {
      width: 20px;
      height: 20px;
      filter: grayscale(100%) brightness(0.3); /* Convert coloured SVGs to dark grey */
    }
  }

  &__title {
    font-size: 1.1rem;
    font-weight: 800;
    color: #111827;
    margin-bottom: 0.35rem;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  &__subtitle {
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b7280;
    margin-bottom: 1.25rem;
    display: block;
  }

  &__desc {
    font-size: 0.85rem;
    color: #4b5563;
    line-height: 1.5;
    margin-bottom: 1.5rem;
  }

  &__features {
    list-style: none;
    padding: 0;
    margin: 0 0 2rem 0;
    flex: 1;

    li {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      padding: 0.35rem 0;
      font-size: 0.85rem;
      color: #4b5563;
      line-height: 1.4;

      i {
        font-size: 0.75rem;
        width: 20px;       /* Fixed width */
        height: 20px;      /* Fixed height */
        border-radius: 50% !important; /* Forces perfect circle */
        background: #f3f4f6 !important; /* Minimalist light gray circle */
        color: #111827 !important; /* Dark tick */
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        margin-top: 2px;
      }
    }
  }

  &--hiring .plan-card__features li i { background: #DBEAFE; color: #2563EB; }
  &--fitment .plan-card__features li i { background: #FEF9C3; color: $brand-hover; }
  &--development .plan-card__features li i { background: #EDE9FE; color: #7C3AED; }
  &--succession .plan-card__features li i { background: #D1FAE5; color: #059669; }

  &__btn {
    width: 100%;
    padding: 0.85rem 1rem;
    border-radius: 12px;
    border: none;
    font-weight: 700;
    font-size: 1rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    color: white;
    margin-top: auto;
  }

    &__btn {
    width: 100%;
    padding: 0.6rem 1rem;
    border-radius: 6px;
    border: 1px solid;
    background: transparent;
    font-weight: 600;
    font-size: 0.85rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    margin-top: auto;

    &--hiring {
      border-color: #2563EB;
      color: #2563EB;
      &:hover { background: #2563EB; color: white; }
    }
    
    &--fitment {
      border-color: #FBBF15;
      color: #FBBF15;
      &:hover { background: #FBBF15; color: white; }
    }
    
    &--development {
      border-color: #7C3AED;
      color: #7C3AED;
      &:hover { background: #7C3AED; color: white; }
    }
    
    &--succession {
      border-color: #059669;
      color: #059669;
      &:hover { background: #059669; color: white; }
    }
  }
}

/* ── Active Plans ── */
.active-plans {
  margin-top: 0.5rem;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1.5rem;
    gap: 1rem;

    @media (max-width: 480px) {
      flex-direction: column;
    }
  }

  &__title {
    font-size: 1.35rem;
    font-weight: 800;
    color: #111827;
    margin-bottom: 4px;

    @media (max-width: 640px) {
      font-size: 1.15rem;
    }
  }

  &__subtitle {
    font-size: 0.9rem;
    color: #9ca3af;

    @media (max-width: 640px) {
      font-size: 0.8rem;
    }
  }

  &__badge {
    background: $brand;
    color: #fff;
    font-size: 0.8rem;
    font-weight: 800;
    padding: 6px 14px;
    border-radius: 50px;
    flex-shrink: 0;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    background: white;
    border-radius: 20px;
    border: 2px dashed #e5e7eb;

    h4 {
      font-size: 1.05rem;
      font-weight: 700;
      color: #374151;
      margin-top: 1rem;
      margin-bottom: 0.25rem;
    }

    p {
      font-size: 0.875rem;
      color: #9ca3af;
    }
  }

  &__loader {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: linear-gradient(135deg, #FEF3C7, #FDE68A);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.75rem;

    i { font-size: 1.25rem; color: $brand-hover; }
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3.5rem 2rem;

    span {
      font-size: 0.875rem;
      color: #9ca3af;
    }
  }

  &__list {
    background: white;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.02);
  }
}

.ap-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1.25rem;
  cursor: pointer;
  transition: background 0.15s ease;
  border-bottom: 1px solid #f3f4f6;

  &:last-child { border-bottom: none; }

  &:hover {
    background: #FAFAFA;
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 0.875rem;
    min-width: 0;
    flex: 1;
  }

   &__icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &.theme-hiring { background: #DBEAFE; i { color: #2563EB; } }
    &.theme-fitment { background: #FEF9C3; i { color: #E8AE08; } }
    &.theme-development { background: #EDE9FE; i { color: #7C3AED; } }
    &.theme-succession { background: #D1FAE5; i { color: #059669; } }
    &.theme-custom { background: #f3f4f6; i { color: #6b7280; } }

    i { font-size: 1rem; }
  }

  &__info {
    min-width: 0;
  }

  &__name {
    font-size: 0.925rem;
    font-weight: 650;
    color: #111827;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
  }

  &__meta {
    font-size: 0.75rem;
    color: #9ca3af;
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 1px;

    i { font-size: 0.7rem; }
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
    margin-left: 1rem;
  }

  &__status {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 3px 8px;
    border-radius: 50px;
    font-weight: 700;
    font-size: 0.625rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    background: #ECFDF5;
    color: #047857;

    i { font-size: 0.55rem; }

    @media (max-width: 480px) { display: none; }
  }

  &__manage {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1.5px solid #f3f4f6;
    background: white;
    color: #9ca3af;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;

    i { font-size: 0.85rem; }

    &:hover {
      border-color: #d1d5db;
      color: #374151;
      background: #f9fafb;
    }
  }

  &__open {
    padding: 0.4rem 0.85rem;
    border-radius: 8px;
    border: none;
    background: linear-gradient(135deg, $brand, $brand-hover);
    color: white;
    font-weight: 700;
    font-size: 0.75rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 4px;
    transition: all 0.15s;
    white-space: nowrap;

    &:hover {
      box-shadow: 0 3px 10px rgba($brand, 0.35);
    }

    &--pending {
      background: linear-gradient(135deg, #F59E0B, #D97706);
      &:hover { box-shadow: 0 3px 10px rgba(#D97706, 0.35); }
    }

    i { font-size: 0.75rem; }

    @media (max-width: 480px) {
      padding: 0.35rem 0.6rem;
      font-size: 0.7rem;
    }
  }

  &--pending {
    opacity: 0.85;
  }

  &__icon--pending {
    background: linear-gradient(135deg, #FEF3C7, #FDE68A) !important;
    i { color: #D97706 !important; }
  }

  &__status--pending {
    background: #FEF3C7 !important;
    color: #D97706 !important;
  }
}

/* Inside or below .ap-row__info */
.ap-row__meta-wrapper {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}

.ap-row__meta {
  font-size: 0.775rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 4px;

  i { font-size: 0.75rem; color: #9ca3af; }

  &--type {
    background: #f3f4f6;
    padding: 2px 8px;
    border-radius: 6px;
    font-weight: 600;
    font-size: 0.7rem;
    color: #4b5563;

    i { color: #6b7280; }
  }
}

.ap-row__divider {
  font-size: 0.7rem;
  color: #d1d5db;
}

/* Ensure the title has a bit more visual weight */
.ap-row__name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

/* ── AI Wizard ── */
.wiz__dots {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 0;
  margin-bottom: 2rem;
  padding: 1.5rem 2.5rem 0;
}
.wiz__dot {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;

  span {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #f3f4f6;
    color: #d1d5db;
    font-size: 0.7rem;
    font-weight: 800;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);

    i { font-size: 0.65rem; }
  }
  p {
    font-size: 0.6rem;
    font-weight: 700;
    color: #d1d5db;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    transition: color 0.3s ease;
  }

  &--done span { background: #D1FAE5; color: #059669; }
  &--done p { color: #9ca3af; }
  &--current span {
    background: linear-gradient(135deg, $brand, $brand-hover);
    color: white;
    box-shadow: 0 3px 12px rgba($brand, 0.35);
    transform: scale(1.08);
  }
  &--current p { color: #111827; }
}
.wiz__dot-line {
  width: 52px;
  height: 2px;
  background: #f3f4f6;
  margin-top: 14px;
  border-radius: 2px;
  transition: background 0.4s ease;
  position: relative;
  overflow: hidden;

  &--active {
    background: #FDE68A;
  }
}

.wiz__step {
  padding: 0 2.5rem;
  animation: wizFadeIn 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}
@keyframes wizFadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.wiz__hero {
  text-align: center;
  margin-bottom: 1.5rem;

  &--compact { margin-bottom: 1.25rem; }
}
.wiz__hero-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(135deg, #FEF3C7, #FDE68A);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.65rem;

  i { font-size: 1.25rem; color: #92400E; }
}
.wiz__title {
  font-size: 1.25rem;
  font-weight: 800;
  color: #111827;
  margin-bottom: 4px;
  letter-spacing: -0.01em;
}
.wiz__subtitle {
  font-size: 0.85rem;
  color: #9ca3af;
  max-width: 360px;
  margin: 0 auto;
  line-height: 1.55;
}

.wiz__input-group {
  margin-bottom: 1.125rem;
}
.wiz__label {
  display: block;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6b7280;
  margin-bottom: 0.4rem;
}
.wiz__input {
  width: 100%;
  padding: 0.65rem 0.875rem;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #111827;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  background: white;
  font-family: inherit;

  &:focus { border-color: $brand; box-shadow: 0 0 0 3px rgba($brand, 0.08); }
  &::placeholder { color: #d1d5db; }
}
.wiz__textarea {
  width: 100%;
  padding: 0.65rem 0.875rem;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 0.85rem;
  color: #111827;
  outline: none;
  resize: none;
  min-height: 96px;
  transition: border-color 0.2s, box-shadow 0.2s;
  font-family: inherit;
  line-height: 1.6;
  background: white;

  &:focus { border-color: $brand; box-shadow: 0 0 0 3px rgba($brand, 0.08); }
  &::placeholder { color: #d1d5db; font-size: 0.8rem; }
}
.wiz__textarea-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 3px;
}
.wiz__char-count {
  font-size: 0.65rem;
  color: #d1d5db;
  font-weight: 600;

  &--warn { color: #f59e0b; }
}

.wiz__suggestions {
  margin-top: 0.125rem;
}
.wiz__suggestions-label {
  font-size: 0.7rem;
  color: #d1d5db;
  margin-bottom: 0.4rem;
  font-weight: 600;
}
.wiz__suggestion-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}
.wiz__suggestion {
  padding: 0.35rem 0.7rem;
  border-radius: 50px;
  border: 1.5px solid #e5e7eb;
  background: white;
  color: #6b7280;
  font-size: 0.725rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: all 0.2s;

  i { font-size: 0.65rem; color: #9ca3af; transition: color 0.2s; }

  &:hover {
    border-color: $brand;
    color: $brand-hover;
    background: #FFFBEB;
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);

    i { color: $brand; }
  }
}

/* Generating state */
.wiz__generating {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3.5rem 2rem;
}
.wiz__generating-pulse {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, $brand, $brand-hover);
  margin-bottom: 1rem;
  position: relative;
  animation: wizPulse 1.8s cubic-bezier(0.4, 0, 0.6, 1) infinite;

  &::after {
    content: '';
    position: absolute;
    inset: -6px;
    border-radius: 50%;
    border: 2px solid rgba($brand, 0.15);
    animation: wizRing 1.8s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
}
@keyframes wizPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}
@keyframes wizRing {
  0%, 100% { transform: scale(1); opacity: 0.6; }
  50% { transform: scale(1.2); opacity: 0; }
}
.wiz__generating-text {
  font-size: 0.95rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 2px;
}
.wiz__generating-sub {
  font-size: 0.775rem;
  color: #d1d5db;
}

.wiz__textarea--hero {
  min-height: 120px;
  font-size: 0.875rem;
}
.wiz__name-suggestion {
  margin-bottom: 1.25rem;
}

/* Journey timeline */
.wiz__journey {
  padding-left: 0.125rem;
  margin-bottom: 0.25rem;
}
.wiz__jstep {
  display: flex;
  gap: 0.875rem;
  min-height: 70px;

  &--last { min-height: auto; }
  &--add { min-height: auto; margin-top: -4px; }
}
.wiz__jstep-marker {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}
.wiz__jstep-circle {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: linear-gradient(135deg, $brand, $brand-hover);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  z-index: 1;
  box-shadow: 0 2px 6px rgba($brand, 0.2);

  i { font-size: 0.65rem; }

  &--add {
    width: 24px;
    height: 24px;
    background: white;
    border: 2px dashed #d1d5db;
    color: #d1d5db;
    box-shadow: none;
    cursor: pointer;
    transition: all 0.2s;

    &:hover { border-color: $brand; color: $brand-hover; }
    i { font-size: 0.6rem; }
  }
}
.wiz__jstep-line {
  width: 2px;
  flex: 1;
  background: linear-gradient(180deg, #FDE68A, #FEF3C7);
  margin: 2px 0;
  border-radius: 1px;
}
.wiz__jstep-content {
  flex: 1;
  padding-bottom: 0.75rem;
  min-width: 0;
}
.wiz__jstep-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1px;
}
.wiz__jstep-num {
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #D97706;
}
.wiz__jstep-remove {
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  color: #d1d5db;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.15s;

  &:hover { background: #FEE2E2; color: #EF4444; }
  i { font-size: 0.7rem; }
}
.wiz__jstep-title {
  width: 100%;
  background: transparent;
  border: none;
  border-bottom: 1.5px solid transparent;
  font-size: 0.875rem;
  font-weight: 700;
  color: #111827;
  outline: none;
  padding: 1px 0;
  font-family: inherit;
  transition: border-color 0.2s, background 0.2s;
  border-radius: 4px;

  &:hover { border-bottom-color: #e5e7eb; }
  &:focus { border-bottom-color: $brand; background: rgba($brand, 0.03); padding: 1px 4px; }
}
.wiz__jstep-desc {
  font-size: 0.75rem;
  color: #9ca3af;
  line-height: 1.4;
  margin-top: 1px;
}
.wiz__jstep-desc-edit {
  width: 100%;
  background: transparent;
  border: none;
  border-bottom: 1px solid transparent;
  font-size: 0.75rem;
  color: #9ca3af;
  outline: none;
  padding: 1px 0;
  font-family: inherit;
  line-height: 1.4;
  margin-top: 1px;
  transition: border-color 0.2s, color 0.2s;

  &:hover { border-bottom-color: #e5e7eb; }
  &:focus { border-bottom-color: $brand; color: #6b7280; }
  &::placeholder { color: #d1d5db; }
}
.wiz__jstep-add-btn {
  background: none;
  border: none;
  color: #9ca3af;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 0;
  transition: color 0.15s;

  &:hover { color: $brand-hover; }
}
.wiz__journey-hint {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 1rem;
  padding: 0.65rem 0.85rem;
  background: #F9FAFB;
  border-radius: 10px;
  font-size: 0.75rem;
  color: #9ca3af;
  line-height: 1.5;

  i { font-size: 0.8rem; flex-shrink: 0; margin-top: 1px; color: #d1d5db; }
}

/* Summary card */
.wiz__summary {
  background: #FAFAFA;
  border-radius: 12px;
  padding: 0.875rem 1.125rem;
  margin-bottom: 0.875rem;
  border: 1px solid #f3f4f6;
}
.wiz__summary-title {
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9ca3af;
  margin-bottom: 0.5rem;
}
.wiz__summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
  color: #374151;
  padding: 0.2rem 0;

  i { font-size: 0.75rem; color: #d1d5db; margin-right: 6px; }

  &--total {
    font-weight: 700;
    color: #111827;
    padding-top: 0.4rem;
  }
}
.wiz__summary-divider {
  height: 1px;
  background: #f3f4f6;
  margin: 0.4rem 0;
}

/* Team size */
.wiz__team-size {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 0.65rem;
}
.wiz__size-control {
  display: flex;
  align-items: center;
  background: white;
  border-radius: 12px;
  border: 2px solid #e5e7eb;
  overflow: hidden;
  transition: border-color 0.2s;

  &:focus-within { border-color: $brand; }
}
.wiz__size-btn {
  width: 38px;
  height: 38px;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover { background: #f9fafb; color: #111827; }
  &:active { background: #f3f4f6; }
  i { font-size: 0.9rem; }
}
.wiz__size-input {
  width: 56px;
  text-align: center;
  border: none;
  background: transparent;
  font-size: 1.2rem;
  font-weight: 800;
  color: #111827;
  outline: none;
  font-family: inherit;
  -moz-appearance: textfield;

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
}
.wiz__size-label {
  font-size: 0.8rem;
  color: #9ca3af;
  font-weight: 600;
}
.wiz__quick-sizes {
  display: flex;
  justify-content: center;
  gap: 0.4rem;
  margin-bottom: 1.125rem;
}
.wiz__quick-size {
  padding: 0.25rem 0.8rem;
  border-radius: 50px;
  border: 1.5px solid #e5e7eb;
  background: white;
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;

  &--active {
    border-color: $brand;
    background: #FFFBEB;
    color: $brand-hover;
    box-shadow: 0 1px 4px rgba($brand, 0.15);
  }
  &:hover:not(&--active):not(:disabled) {
    border-color: #d1d5db;
    background: #f9fafb;
  }

  &--disabled, &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
}
.wiz__pricing-total {
  font-size: 1.1rem;
  font-weight: 800;
  color: #111827;

  small { font-size: 0.7rem; font-weight: 600; color: #9ca3af; }
}

/* People picker */
.wiz__people-section {
  margin-bottom: 0.5rem;
}
.wiz__people-toggle {
  width: 100%;
  padding: 0.65rem 0.875rem;
  border-radius: 10px;
  border: 1.5px solid #e5e7eb;
  background: white;
  color: #6b7280;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s;

  &:hover { border-color: #d1d5db; background: #fafafa; }
}
.wiz__people-toggle-left {
  display: flex;
  align-items: center;
  gap: 8px;

  i { font-size: 0.85rem; color: #9ca3af; }
}
.wiz__people-toggle-right {
  display: flex;
  align-items: center;
  gap: 6px;

  i { font-size: 0.8rem; color: #d1d5db; }
}
.wiz__people-count {
  background: #D1FAE5;
  color: #059669;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 50px;
}
.wiz__people-picker {
  margin-top: 0.625rem;
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  animation: wizFadeIn 0.25s ease;
}
.wiz__people-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.6rem 0.875rem;
  border-bottom: 1px solid #f3f4f6;
  background: #fafafa;

  i { font-size: 0.85rem; color: #d1d5db; flex-shrink: 0; }

  input {
    flex: 1;
    border: none;
    background: transparent;
    font-size: 0.825rem;
    color: #111827;
    outline: none;
    font-family: inherit;

    &::placeholder { color: #d1d5db; }
  }
}
.wiz__people-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0.875rem;
  border-bottom: 1px solid #f3f4f6;
  background: white;

  span {
    font-size: 0.7rem;
    font-weight: 600;
    color: #9ca3af;
  }

  button {
    font-size: 0.7rem;
    font-weight: 600;
    color: $brand-hover;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;

    &:hover { text-decoration: underline; }
  }
}
.wiz__people-list {
  max-height: 220px;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 2px; }
}
.wiz__person {
  display: flex;
  align-items: center;
  padding: 0.6rem 0.875rem;
  gap: 0.65rem;
  cursor: pointer;
  transition: background 0.1s;
  border-bottom: 1px solid #f9fafb;

  &:last-child { border-bottom: none; }
  &:hover { background: #fafafa; }

  &--selected {
    background: #FFFBEB;
    &:hover { background: #FEF9C3; }

    .wiz__person-check {
      background: $brand;
      border-color: $brand;
      color: white;
    }
  }
}
.wiz__person-check {
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 2px solid #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;
  color: transparent;

  i { font-size: 0.6rem; }
}
.wiz__person-info {
  min-width: 0;
  flex: 1;
}
.wiz__person-name {
  font-size: 0.825rem;
  font-weight: 600;
  color: #111827;
  line-height: 1.3;
}
.wiz__person-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.725rem;
  margin-top: 1px;
}
.wiz__person-role {
  padding: 1px 6px;
  border-radius: 4px;
  background: #f3f4f6;
  color: #6b7280;
  font-weight: 600;
  font-size: 0.65rem;
}
.wiz__person-email {
  color: #9ca3af;
}
.wiz__people-empty {
  padding: 2rem;
  text-align: center;
  color: #d1d5db;

  i { font-size: 1.25rem; margin-bottom: 0.4rem; display: block; }
  p { font-size: 0.8rem; }
}

/* Payment */
.wiz__payment {
  margin-top: 0.875rem;
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  animation: wizFadeIn 0.25s ease;
}
.wiz__payment-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.6rem 0.875rem;
  background: #fafafa;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.75rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  i { font-size: 0.8rem; color: #9ca3af; }
}
.wiz__stripe-card {
  padding: 0.875rem;
  background: white;
}
.wiz__payment-error {
  padding: 0.5rem 0.875rem;
  font-size: 0.775rem;
  color: #EF4444;
  display: flex;
  align-items: center;
  gap: 5px;
  background: #FEF2F2;

  i { font-size: 0.75rem; }
}

/* Wizard footer */
.wiz__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2.5rem 1.5rem;
  margin-top: 0.5rem;
}
.wiz__back {
  padding: 0.55rem 0.9rem;
  border-radius: 10px;
  border: none;
  background: #f3f4f6;
  color: #6b7280;
  font-weight: 600;
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.2s;

  &:hover { background: #e5e7eb; color: #374151; }
  i { font-size: 0.8rem; }
}
.wiz__next {
  padding: 0.6rem 1.4rem;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, $brand, $brand-hover);
  color: white;
  font-weight: 700;
  font-size: 0.825rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba($brand, 0.2);

  &:hover:not(:disabled) {
    box-shadow: 0 4px 16px rgba($brand, 0.35);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  &--ready {
    padding: 0.6rem 1.75rem;
    animation: wizGlow 2.5s ease-in-out infinite;
  }

  i { font-size: 0.8rem; }
}
@keyframes wizGlow {
  0%, 100% { box-shadow: 0 2px 8px rgba($brand, 0.2); }
  50% { box-shadow: 0 4px 20px rgba($brand, 0.4); }
}

/* ── Guides Section ── */
.guides {
  margin-top: 0.5rem;

  &__header {
    margin-bottom: 1.5rem;
  }

  &__title {
    font-size: 1.35rem;
    font-weight: 800;
    color: #111827;
    margin-bottom: 4px;
  }

  &__subtitle {
    font-size: 0.9rem;
    color: #9ca3af;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;

    @media (max-width: 900px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 600px) {
      grid-template-columns: 1fr;
    }
  }
}

.guide-card {
  text-decoration: none;
  color: inherit;
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease;

  &:hover {
    .guide-card__thumb {
      img { transform: scale(1.03); }
    }

    .guide-card__play {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }

    .guide-card__body h4 {
      color: $brand-hover;
    }
  }

  &__thumb {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    border-radius: 12px;
    background: #111827;
    cursor: pointer;

    img, iframe {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border: none;
    }

    img {
      transition: transform 0.3s ease;
    }
  }

  &__play {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0.9);
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.15rem;
    opacity: 0.8;
    transition: all 0.2s ease;
    backdrop-filter: blur(4px);

    i { margin-left: 2px; }
  }

  &__duration {
    position: absolute;
    bottom: 8px;
    right: 8px;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    font-size: 0.675rem;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: 4px;
    letter-spacing: 0.02em;
  }

  &__body {
    padding: 0.75rem 0.25rem;
    text-align: left;

    h4 {
      font-size: 0.95rem;
      font-weight: 700;
      color: #111827;
      margin-bottom: 4px;
      line-height: 1.35;
      transition: color 0.15s;
    }

    p {
      font-size: 0.825rem;
      color: #9ca3af;
      line-height: 1.45;
    }
  }
}

.guides__help {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-top: 0.75rem;
  padding: 0.625rem 1rem;
  background: #f9fafb;
  border-radius: 12px;
  font-size: 0.9rem;
  color: #6b7280;

  i {
    font-size: 1.1rem;
    color: #9ca3af;
    flex-shrink: 0;
  }

  a {
    color: $brand-hover;
    font-weight: 700;
    text-decoration: none;

    &:hover { text-decoration: underline; }
  }
}

/* ── Guides Modal ── */
.guides-popup {
  ::v-deep .vs-popup {
    width: 680px;
    max-width: 90vw;
    border-radius: 16px;
  }
}

.guides-modal {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 0.5rem;

  &__item {
    display: flex;
    gap: 1rem;
    align-items: flex-start;

    .guide-card__thumb {
      width: 220px;
      min-width: 220px;
      aspect-ratio: 16 / 9;

      @media (max-width: 500px) {
        width: 100%;
        min-width: unset;
      }
    }

    .guide-card__body {
      padding: 0;
    }

    @media (max-width: 500px) {
      flex-direction: column;
    }
  }
}

/* ── Scrollbar ── */
.custom-scroll {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.custom-scroll::-webkit-scrollbar { display: none; }

.wiz__people-list {
  max-height: 220px;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar { display: none; }
}

/* ── Contact Modal ── */
.contact-popup {
  ::v-deep .vs-popup {
    width: 420px;
    max-width: 90vw;
    border-radius: 20px;
  }

  ::v-deep .vs-popup--header {
    display: none;
  }

  ::v-deep .vs-popup--content {
    padding: 0 !important;
    margin: 0 !important;
  }
}

.contact-modal {
  text-align: left;
  padding: 2rem 2.25rem;

  h3 {
    font-size: 1.25rem;
    font-weight: 800;
    color: #111827;
    margin-bottom: 0.625rem;
  }

  p {
    font-size: 0.9rem;
    color: #6b7280;
    line-height: 1.55;
    margin-bottom: 1.5rem;
  }

  &__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 0.75rem 1.5rem;
    border-radius: 12px;
    background: linear-gradient(135deg, $brand, $brand-hover);
    color: white;
    font-weight: 700;
    font-size: 0.9rem;
    text-decoration: none;
    transition: all 0.15s;
    box-shadow: 0 2px 8px rgba($brand, 0.25);

    &:hover {
      box-shadow: 0 4px 14px rgba($brand, 0.4);
      transform: translateY(-1px);
      color: white;
    }
  }

  &__close {
    display: block;
    width: 100%;
    margin-top: 0.75rem;
    padding: 0.6rem;
    background: none;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    color: #6b7280;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;

    &:hover { border-color: #d1d5db; background: #f9fafb; color: #374151; }
  }
}

/* ── Configure Plan Modal ── */
.configure-popup {
  ::v-deep .vs-popup {
    width: 640px;
    max-width: 95vw;
    border-radius: 20px;
    overflow: hidden !important; 
  }

  /* ADD THESE LINES TO HIDE THE DEFAULT 'X' */
  ::v-deep .vs-popup--close {
    display: none !important;
  }

  ::v-deep .vs-popup--header {
    display: none !important;
  }

  ::v-deep .vs-popup--content {
    padding: 0 !important;
    margin: 0 !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    max-height: 85vh; 
    
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
}

.cfg {
  position: relative;
  text-align: left;

  &__close-btn {
    position: absolute;
    top: 1.25rem;
    right: 1.25rem;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #f3f4f6;
    border: none;
    color: #6b7280;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    z-index: 50;

    &:hover {
      background: #e5e7eb;
      color: #111827;
      transform: scale(1.05);
    }

    i {
      font-size: 1.1rem;
    }
  }

  &--loading { pointer-events: none; }

  &__overlay {
    position: absolute;
    inset: 0;
    background: rgba(255,255,255,0.92);
    z-index: 50;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 20px;

    i { font-size: 1.75rem; color: $brand; margin-bottom: 0.75rem; }
    span { font-size: 0.9rem; color: #6b7280; font-weight: 600; }
  }

  /* Header */
  &__header {
    padding: 1.5rem 2.5rem 0;
  }

  &__title {
    font-size: 1.35rem;
    font-weight: 800;
    color: #111827;
    margin-bottom: 3px;
  }

  &__desc {
    font-size: 0.9rem;
    color: #9ca3af;
  }

  /* Sections */
  &__section {
    padding: 0.75rem 2.5rem;
    margin: 0.25rem 0;
    border-left: 3px solid transparent;
    transition: all 0.25s;

    &--active {
      border-left-color: $brand;
      background: linear-gradient(90deg, rgba($brand, 0.04), transparent 60%);
    }
  }

  &__step-indicator {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.7rem;
    font-weight: 700;
    color: $brand-hover;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 0.75rem;
  }

  &__step-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: $brand;
    animation: cfg-pulse 1.5s ease-in-out infinite;
  }

  @keyframes cfg-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(0.8); }
  }

  &__label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.75rem;
    font-weight: 700;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 6px;

    i { font-size: 0.8rem; color: #9ca3af; }
  }

  &__label-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
  }

  &__readonly-value {
    font-size: 1.1rem;
    font-weight: 700;
    color: #111827;
    padding: 0.125rem 0;
  }

  &__hint {
    font-size: 0.85rem;
    color: #9ca3af;
    margin-bottom: 0.5rem;
    line-height: 1.45;
  }

  &__selected-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.75rem;
    font-weight: 700;
    color: #059669;
    background: #ECFDF5;
    padding: 3px 10px;
    border-radius: 50px;

    i { font-size: 0.65rem; }
  }

  &__search {
    margin-bottom: 0.5rem;
  }

  /* Filter */
  &__controls {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 1rem;
    padding-right: 2px;
  }

  &__search {
    margin-bottom: 0 !important;
  }

  &__filter-wrapper {
    position: relative;
  }

  &__filter-icon-btn {
    position: relative;
    width: 38px;
    height: 38px;
    border-radius: 10px;
    border: 2px solid #e5e7eb;
    background: white;
    color: #6b7280;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;

    &:hover { border-color: $brand; color: $brand; background: #FFFBEB; }
    &--active { border-color: $brand; color: $brand; background: #FFFBEB; }
    
    i { font-size: 1.1rem; }
  }

  &__filter-badge {
    position: absolute;
    top: -6px;
    right: -6px;
    background: $brand;
    color: #111827;
    font-size: 0.65rem;
    font-weight: 800;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  /* Floating Dropdown Menu */
  &__dropdown-menu {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    width: 250px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    z-index: 99999;
    overflow: hidden;
  }

  &__dropdown-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.6rem 1rem;
    background: #f9fafb;
    border-bottom: 1px solid #f3f4f6;
  }

  &__dropdown-selected-text {
    font-size: 0.75rem;
    color: #6b7280;
    font-weight: 700;
  }

  &__filter-clear {
    font-size: 0.75rem;
    font-weight: 600;
    color: #9ca3af;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;

    &:hover { color: #ef4444; }
  }

  &__dropdown-list {
    max-height: 220px;
    overflow-y: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar { display: none; }
  }

  &__dropdown-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0.7rem 1rem;
    font-size: 0.85rem;
    color: #374151;
    cursor: pointer;
    transition: background 0.15s;
    border-bottom: 1px solid #f9fafb;

    &:last-child { border-bottom: none; }
    &:hover { background: #f9fafb; }
  }

  /* Fixed Checkbox Styling */
  &__user-check, &__role-checkbox {
    width: 20px;
    height: 20px;
    border-radius: 6px;
    border: 2px solid #d1d5db;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.15s;
    background: white;
    color: transparent;

    i { 
      font-size: 0.8rem; 
      color: white !important; 
      font-weight: bold;
    }

    &--active {
      background: $brand;
      border-color: $brand;
    }
  }

  /* Employee list */
  &__list-wrap {
    padding: 0 2.5rem;
    margin-top: 0.5rem;
  }

  &__list-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
    padding: 0 0.5rem;

    span {
      font-size: 0.8rem;
      font-weight: 600;
      color: #9ca3af;
    }
  }

  &__select-all {
    font-size: 0.775rem;
    font-weight: 600;
    color: $brand-hover;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;

    &:hover { text-decoration: underline; }
  }

  &__list {
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    max-height: 30vh; /* <--- CHANGED from 320px */
    overflow-y: auto;

    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 2px; }
  }

  &__user {
    display: flex;
    align-items: center;
    padding: 0.875rem 1.125rem;
    gap: 0.875rem;
    cursor: pointer;
    transition: background 0.1s;
    border-bottom: 1px solid #f3f4f6;

    &:last-child { border-bottom: none; }
    &:hover { background: #fafafa; }

    &--selected {
      background: #FFFBEB;
      &:hover { background: #FEF9C3; }

      &__user-check {
        width: 22px;
        height: 22px;
        border-radius: 6px;
        border: 2px solid #d1d5db;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        transition: all 0.15s;
        font-size: 0.7rem;
        color: transparent;

        &--active {
          background: $brand;
          border-color: $brand;
          color: white;
        }
      }
    }
  }

  &__user-check {
    width: 22px;
    height: 22px;
    border-radius: 6px;
    border: 2px solid #d1d5db;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.15s;
    font-size: 0.7rem;
    color: transparent;
  }

  &__user-info {
    min-width: 0;
    flex: 1;
  }

  &__user-name {
    font-size: 0.9rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 3px;
  }

  &__user-details {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.8rem;
  }

  &__user-role {
    padding: 2px 8px;
    border-radius: 5px;
    background: #f3f4f6;
    color: #6b7280;
    font-weight: 600;
    font-size: 0.7rem;
  }

  &__user-email {
    color: #9ca3af;
  }

  &__empty {
    padding: 2.5rem;
    text-align: center;
    color: #9ca3af;

    i { font-size: 1.5rem; margin-bottom: 0.5rem; display: block; }
    p { font-size: 0.875rem; }
  }

  /* Footer */
  &__footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.75rem;
    
    /* CHANGED: Increased bottom padding from 1rem to 1.5rem (or 2rem) */
    padding: 1rem 2.5rem 1.5rem; 
    
    border-top: 1px solid #f3f4f6;
    margin-top: 0.75rem;
  }


  &__cancel {
    padding: 0.7rem 1.5rem;
    border-radius: 12px;
    border: 2px solid #e5e7eb;
    background: white;
    color: #6b7280;
    font-weight: 600;
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.15s;

    &:hover { border-color: #d1d5db; background: #f9fafb; color: #374151; }
  }

  &__submit {
    padding: 0.7rem 1.75rem;
    border-radius: 12px;
    border: none;
    background: linear-gradient(135deg, $brand, $brand-hover);
    color: white;
    font-weight: 700;
    font-size: 0.9rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.15s;
    box-shadow: 0 2px 8px rgba($brand, 0.25);

    &:hover:not(:disabled) {
      box-shadow: 0 4px 14px rgba($brand, 0.4);
      transform: translateY(-1px);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    &--ready {
      animation: cfg-glow 2s ease-in-out infinite;
    }
  }

  /* Payment */
  &__price-summary {
    background: #f9fafb;
    border-radius: 10px;
    padding: 0.875rem 1rem;
    margin-bottom: 1rem;
  }

  &__price-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.9rem;
    color: #374151;
  }

  &__price-amount {
    font-weight: 800;
    color: #111827;
    font-size: 1.05rem;
  }

  &__stripe-card {
    border: 2px solid #e5e7eb;
    border-radius: 10px;
    padding: 0.875rem 1rem;
    background: white;
    transition: border-color 0.2s;

    &:focus-within {
      border-color: $brand;
      box-shadow: 0 0 0 3px rgba($brand, 0.1);
    }
  }

  &__payment-error {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 0.625rem;
    font-size: 0.825rem;
    color: #EF4444;
    font-weight: 600;

    i { font-size: 0.85rem; }
  }

  /* Expand animation */
  &-expand-enter-active {
    animation: cfg-slide-down 0.4s ease-out;
  }

  &-expand-leave-active {
    animation: cfg-slide-down 0.25s ease-in reverse;
  }

  @keyframes cfg-slide-down {
    0% {
      opacity: 0;
      max-height: 0;
      transform: translateY(-12px);
      overflow: hidden;
    }
    100% {
      opacity: 1;
      max-height: 800px;
      transform: translateY(0);
      overflow: visible;
    }
  }

  @keyframes cfg-glow {
    0%, 100% { box-shadow: 0 2px 8px rgba($brand, 0.25); }
    50% { box-shadow: 0 4px 20px rgba($brand, 0.5); }
  }
}

.badge-free {
  background: #D1FAE5;
  color: #059669;
  font-size: 0.65rem;
  padding: 2px 8px;
  border-radius: 50px;
  margin-left: 8px;
}

.badge-paid {
  background: #FFEDD5;
  color: #C2410C;
  font-size: 0.65rem;
  padding: 2px 8px;
  border-radius: 50px;
  margin-left: 8px;
}

.plan-cards {
  display: grid;
  /* Updated to 4 columns */
  grid-template-columns: repeat(4, 1fr); 
  gap: 1.5rem;
  margin-bottom: 2.5rem;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.plan-list__icon--hiring {
  background: linear-gradient(135deg, #60A5FA, #2563EB);
}

.wiz__step-scrollable {
  max-height: 55vh;
  overflow-y: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  padding-bottom: 1rem;

  &::-webkit-scrollbar {
    display: none; /* Chrome/Safari */
  }
}

.contact-popup {
  ::v-deep .vs-popup {
    width: 440px;
    max-width: 90vw;
    border-radius: 20px;
    text-align: center;
  }

  ::v-deep .vs-popup--header {
    display: none;
  }

  ::v-deep .vs-popup--content {
    padding: 0 !important;
    margin: 0 !important;
  }
}

.contact-modal {
  padding: 2.5rem 2rem;

  h3 {
    font-size: 1.25rem;
    font-weight: 800;
    color: #111827;
    margin-bottom: 0.75rem;
  }

  p {
    font-size: 0.9rem;
    color: #6b7280;
    line-height: 1.55;
    margin-bottom: 1.75rem;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  &__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 0.8rem 1.5rem;
    border-radius: 12px;
    font-weight: 700;
    font-size: 0.95rem;
    cursor: pointer;
    transition: all 0.2s ease;
    text-decoration: none;

    &--primary {
      background: $brand;
      color: white;
      border: none;
      
      &:hover {
        background: $brand-hover;
        box-shadow: 0 4px 14px rgba($brand, 0.3);
        transform: translateY(-1px);
        color: white;
      }
    }

    &--secondary {
      background: white;
      color: #6b7280;
      border: 1.5px solid #e5e7eb;
      
      &:hover {
        border-color: #d1d5db;
        background: #f9fafb;
        color: #374151;
      }
    }
  }
}

$coach-brand: #FBBF15;
$coach-brand-hover: #E8AE08;
$coach-light: #FFFBEB;

/* Make the active section border yellow for coach plans */
.cfg__section--coach.cfg__section--active {
  border-left-color: $coach-brand;
  background: linear-gradient(90deg, rgba($coach-brand, 0.04), transparent 60%);

  .cfg__step-dot { background: $coach-brand; }
  .cfg__step-indicator { color: $coach-brand-hover; }
}

/* Credit Tracker Badge */
.cfg__credit-tracker {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #D97706;
  background: $coach-light;
  padding: 4px 12px;
  border-radius: 50px;
  border: 1px solid #FDE68A;
  transition: all 0.3s ease;

  i { font-size: 0.8rem; }

  &--full {
    background: #FEF2F2;
    color: #EF4444;
    border-color: #FECACA;
  }
}

.cfg__hint--warning {
  color: #EF4444;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;
}

/* User Row Disabled State (When Credits are 0) */
.cfg__user--disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background: #f9fafb;
  
  &:hover { background: #f9fafb; }
}

/* Yellow Checkboxes for Coach Plans */
.cfg__section--coach {
  .cfg__user--selected {
    background: $coach-light;
    
    .cfg__user-check--active {
      background: $coach-brand;
      border-color: $coach-brand;
      color: white;
    }
  }
}

/* Yellow Submit Button */
.cfg__submit--coach {
  background: linear-gradient(135deg, $coach-brand, $coach-brand-hover);
  color: white;
  
  &:hover:not(:disabled) {
    box-shadow: 0 4px 14px rgba($coach-brand, 0.4);
  }
}
.cfg__buy-credits-box {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  margin-bottom: 1rem;

  i {
    color: #EF4444;
    font-size: 1.1rem;
    margin-top: 2px;
  }

  p {
    font-size: 0.8rem;
    color: #991B1B;
    margin: 0 0 4px 0;
    font-weight: 600;
  }

  a {
    font-size: 0.75rem;
    color: #DC2626;
    font-weight: 700;
    text-decoration: underline;
    cursor: pointer;
    
    &:hover { color: #B91C1C; }
  }
}
.icon-btn {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.icon-btn i {
  font-size: 15px;
}

.coach-icon-btn {
  background: #FBBF15;
  color: #fff;
}
.cfg__add-credits-inline-btn {
  background: #D97706;
  border: none;
  color: white !important;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-left: 8px;
  padding: 0;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  i {
    font-size: 0.65rem !important;
    font-weight: 800;
    color: white !important;
  }

  &:hover {
    background: #B45309;
    transform: scale(1.12);
  }
}
.credit-list { 
  display: flex; 
  flex-direction: column; 
  gap: 0.75rem; 
  margin-top: 1.5rem;
}

.credit-row { 
  display: flex; 
  align-items: center; 
  justify-content: space-between; 
  padding: 1rem 1.25rem; 
  border: 1.5px solid #e5e7eb; 
  border-radius: 12px; 
  background: white; 
}

.fw-name { 
  font-weight: 700; 
  color: #111827; 
  display: flex; 
  align-items: center; 
  gap: 10px; 
  font-size: 0.95rem;
  
  i { 
    color: #D97706; /* The distinct orange color */
    font-size: 1.15rem; 
  } 
}

.wiz__size-control { 
  display: flex; 
  align-items: center; 
  background: white; 
  border-radius: 10px; 
  border: 2px solid #e5e7eb; 
  overflow: hidden; 
  transition: border-color 0.2s; 
  
  &:focus-within { 
    border-color: #FBBF15; 
  }
}

.wiz__size-btn { 
  width: 36px; 
  height: 36px; 
  border: none; 
  background: transparent; 
  color: #6b7280; 
  cursor: pointer; 
  font-size: 0.9rem; 
  display: flex; 
  align-items: center; 
  justify-content: center; 
  
  &:hover { 
    background: #f3f4f6; 
    color: #111827; 
  } 
}

.wiz__size-input { 
  width: 50px; 
  text-align: center; 
  border: none; 
  font-size: 1.1rem; 
  font-weight: 800; 
  color: #111827; 
  outline: none; 
  background: transparent; 
  -moz-appearance: textfield; 
  
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button { 
    -webkit-appearance: none; 
    margin: 0; 
  }
}
.dummy-card-element { 
  display: flex; 
  justify-content: space-between; 
  align-items: center; 
  padding: 0.85rem 1rem; 
  border: 1.5px solid #e5e7eb; 
  border-radius: 8px; 
  color: #9ca3af; 
  font-size: 0.9rem; 
  background: #ffffff;
}

.dummy-placeholder {
  color: #9ca3af;
}

.dummy-right { 
  display: flex; 
  gap: 1.5rem; 
  color: #d1d5db;
}
.cfg__more-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1.5px solid #e5e7eb;
  background: white;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #d1d5db;
    color: #111827;
    background: #f9fafb;
  }

  i {
    font-size: 1rem;
  }
}

.cfg__plan-options-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  width: 180px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  z-index: 99999;
  padding: 6px 0;
  overflow: hidden;
}

.cfg__plan-option-item {
  display: flex;
  align-items: center;
  padding: 8px 14px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #f3f4f6;
    color: #111827;
  }

  &--danger {
    color: #ef4444;
    border-top: 1px solid #f3f4f6;
    margin-top: 4px;
    padding-top: 8px;

    &:hover {
      background: #fef2f2;
      color: #dc2626;
    }
  }
}
</style>